/**
 * Chatbot API Service
 * Handles real API communication with the chatbot backend using NewConversationService
 */

import { ResponseStage, ResponseProgress, StructuredContent } from '@stores/chatbot/types';
import NewConversationService, { CreateNewConversationRequest } from '@apis/NewConversationService';
import { useChatbotStore } from '@stores/chatbot/chatbotStore';
import { executiveResourceKey, captureExecutiveSession, ExecutiveResourceMap } from '@stores/chatbot/executiveSession';
import { peekSharedConversationStore } from '@stores/chatbot/sharedConversationStores';
import { statusActivityNote, takeBackAnswer } from '@stores/chatbot/liveTurn';
import { readTurnPending } from '@hooks/chatTurnChime';
import { buildMessageWithAttachments } from '@services/chatbot/buildMessageWithAttachments';
import { extractExecutionTodoFromStreamChunk } from '@services/chatbot/executionTodoState';
import { addToolBreadcrumb, captureChatWarning } from '@libs/sentryChat';
import posthog from '@libs/posthog';
import { EVENTS } from '@lib/analytics';
import { attachAuthHeaders } from '@apis/authHeaders';
import { parseSteerAck } from '@apis/executiveContract';
import { API_BASE_URL } from '@config/config';
import {
  deliveryBeaconIdentity,
  mintDeliveryId,
  mintSendId,
  pageExitBeacons,
  pageNavigationType,
  shouldRehydrateAfterClose,
  shouldReportPageExit,
  streamCloseOutcome,
  type InFlightDelivery,
} from '@services/chatbot/pageExitDelivery';
import {
  WORKER_LOSS_REATTACH_DELAY_MS,
  markDeliveryError,
  savedReplyChars,
  serverTurnState,
  turnChangedRetryDelayMs,
  workerLossRecovery,
} from '@services/chatbot/deliveryRecovery';
import { deferBeacon, takeDeferredBeacons, type DeferredBeacon } from '@services/chatbot/deliveryOutbox';
import { currentUserPin } from '@services/chatbot/tabIdentity';

/**
 * Report how a stream ended to BOTH PostHog and our own API.
 *
 * `answer_rendered` is the only signal that says whether a person actually got
 * their answer, and it was reaching PostHog and nowhere else. When that token
 * expired the question became unanswerable from our own data for a week, while
 * turns kept completing server-side and looking fine. Delivery truth is
 * operational, not analytical: it gates ship decisions, so it belongs in a
 * store we own. PostHog keeps getting the event unchanged — this adds a second
 * sink, it does not move one.
 *
 * `fetch(keepalive)` rather than `navigator.sendBeacon`, for a reason specific
 * to this payload: sendBeacon cannot set headers, and `attachAuthHeaders`
 * carries the Masquerade and X-Workspace-Id pins. Drop those and a beacon sent
 * during a masqueraded session is attributed to the wrong user — which is
 * precisely the session shape the first investigation into this was about.
 * keepalive survives page unload the same way sendBeacon does.
 *
 * Fire-and-forget, and silent on failure. Nothing waits on this and a page
 * that is closing must not be held up by it.
 */
function reportStreamClosed(
  conversationId: string,
  rawPayload: Record<string, unknown>,
  requestHeaders: Readonly<Record<string, string>> | null,
): void {
  // Stamped here, the single send point, so every close path carries it — the
  // pagehide beacon included, which is the one fired by a document on its way out.
  let navigationType: ReturnType<typeof pageNavigationType> | undefined;
  try { navigationType = pageNavigationType(); } catch { /* telemetry is best effort */ }
  const payload = { ...rawPayload, page_nav_type: navigationType };
  try {
    posthog.capture(EVENTS.CONVERSATION.STREAM_CLOSED, {
      conversation_id: conversationId,
      ...payload,
    });
  } catch {
    /* The independent delivery record must survive an analytics SDK failure. */
  }

  // A failed header snapshot must never fall back to the current/wrong tenant.
  if (!requestHeaders) return;
  postDeliveryReceipt(conversationId, payload, requestHeaders);
}

/**
 * File one receipt. A 401 queues it for the next page that can file it.
 *
 * The endpoint is authenticated, so a send refused for a missing cookie has
 * its receipt refused by the same missing cookie — the one failure that could
 * never reach the delivery rate. Queued instead, it is filed by the first
 * receipt the server accepts, which on the Reconnect path is the replayed
 * send's own. `prior` carries a re-filed receipt's history so a jar that keeps
 * dropping the cookie stops being retried.
 */
function postDeliveryReceipt(
  conversationId: string,
  payload: Record<string, unknown>,
  requestHeaders: Readonly<Record<string, string>>,
  prior?: Pick<DeferredBeacon, 'tries' | 'deferredAtMs' | 'userId'>,
): void {
  try {
    // encodeURIComponent even though ids are server-minted and the server now
    // shape-checks this one: an un-encoded interpolation into a URL path is a
    // habit that survives the day someone routes a user-supplied id here.
    fetch(`${API_BASE_URL}/new-conversations/${encodeURIComponent(conversationId)}/delivery`, {
      method: 'POST',
      headers: requestHeaders,
      credentials: 'include',
      keepalive: true,
      body: JSON.stringify(payload),
    }).then((response) => {
      if (response.status === 401) {
        if (typeof sessionStorage === 'undefined') return;
        deferBeacon(
          { conversationId, payload, headers: { ...requestHeaders }, userId: currentUserPin(), ...prior },
          sessionStorage,
          Date.now(),
        );
      } else if (response.ok) {
        flushDeferredDeliveryReceipts();
      }
    }).catch(() => {
      /* telemetry must never surface to the user */
    });
  } catch {
    /* keepalive unsupported, or the page is already gone */
  }
}

/**
 * File every receipt a 401 held back. Called once the server has accepted a
 * receipt from this tab, and on chat mount for the page that follows a
 * Reconnect. Each is sent exactly as first built — its navigation type
 * describes the page that failed, not this one.
 */
export function flushDeferredDeliveryReceipts(): void {
  if (typeof sessionStorage === 'undefined') return;
  let entries: DeferredBeacon[] = [];
  try {
    entries = takeDeferredBeacons(sessionStorage, Date.now(), currentUserPin());
  } catch {
    return;
  }
  for (const entry of entries) {
    postDeliveryReceipt(entry.conversationId, entry.payload, entry.headers, entry);
  }
}

type DeliveryPhase = 'header_capture' | 'send_preparation' | 'conversation_preparation' | 'stream_preparation' | 'transport';

/**
 * One browser attempt at delivering one typed message. `sendId` is the
 * message: pass the interrupted send's id on Retry, auto-resume and a
 * post-reload replay, so every attempt it takes is counted as the same send.
 */
export function createDeliveryAttempt(conversationId?: string, sendId?: string) {
  const startTime = Date.now();
  const requestId = `req_${startTime}_${Math.random().toString(36).substring(2, 11)}`;
  const messageSendId = sendId || mintSendId(startTime);
  const target = conversationId || `conv_${Math.floor(startTime / 1000)}_${Math.random().toString(36).substring(2, 10)}`;
  let headers: Readonly<Record<string, string>> | null = null;
  try {
    headers = Object.freeze({ ...attachAuthHeaders({ 'Content-Type': 'application/json' }) });
  } catch { /* Do not use a partial snapshot or re-read tenant context later. */ }
  let reported = false;
  const attempt = {
    requestId, startTime, conversationId: target, headers, sendId: messageSendId,
    get reported() { return reported; },
    phase: (headers ? 'send_preparation' : 'header_capture') as DeliveryPhase,
    ensureHeaders() {
      if (!headers) throw new Error('Delivery auth context unavailable');
    },
    report(payload: Record<string, unknown>) {
      if (reported) return;
      reported = true;
      try {
        reportStreamClosed(
          target,
          { ...payload, send_id: messageSendId, delivery_phase: attempt.phase },
          headers,
        );
      } catch { /* Reporting cannot break Send or replace its original error. */ }
    },
    fail(error: unknown) {
      let status: number | undefined;
      let refusal = false;
      try {
        const candidate = error as { status?: number; code?: string } | null;
        if (Number.isInteger(candidate?.status) && candidate!.status! >= 100 && candidate!.status! <= 599) {
          status = candidate!.status;
        }
        refusal = candidate?.code === 'SUBSCRIPTION_REQUIRED';
      } catch { /* A hostile error object must not break its own receipt. */ }
      attempt.report({
        ...deliveryBeaconIdentity(null, requestId, startTime),
        outcome: 'interrupted', interrupt_retryable: true,
        client_error_kind: status || refusal ? 'http' : 'client',
        client_error_status: status, answer_rendered: false, answer_chars: 0,
        last_event_id: 0, reconnect_attempts: 0, total_reconnect_attempts: 0,
        stall_reconnects: 0, had_network_disconnect: false,
        elapsed_ms: Math.max(0, Date.now() - startTime), transport: 'stream',
      });
    },
  };
  return attempt;
}

export type DeliveryAttempt = ReturnType<typeof createDeliveryAttempt>;

/** What a rehydrate needs to report whether it recovered the answer. */
interface RehydrateReceipt {
  turnId: string | null;
  sendId: string;
  headers: Readonly<Record<string, string>>;
  /** Run when the saved conversation loaded and holds no reply. */
  onEmpty?: () => void;
}

export async function withDeliveryAttempt(
  conversationId: string | undefined,
  send: (attempt: DeliveryAttempt) => Promise<void>,
  sendId?: string,
) {
  const attempt = createDeliveryAttempt(conversationId, sendId);
  try {
    attempt.ensureHeaders();
    await send(attempt);
  } catch (error) {
    attempt.fail(error);
    throw error;
  }
}
export interface ChatbotApiResponse {
  id: string;
  content: string | StructuredContent; // Support both string and structured content
  timestamp: number;
  conversationId?: string;
  suggestedResponses?: string[]; // Suggested follow-up responses from the agent
  metadata?: {
    model?: string;
    requires_approval?: boolean;
    requires_questionnaire?: boolean;
    questionnaire_data?: {
      questions: Array<{ id: string; prompt: string; options: string[]; allow_freeform: boolean }>;
      conversation_id: string;
    };
    status?: string;
    usage?: {
      prompt_tokens: number;
      completion_tokens: number;
      total_tokens: number;
    };
    isContinuation?: boolean; // Flag to indicate this is a continuation response
    isInterrupted?: boolean;
    error?: string;
    isRetryable?: boolean;
  };
}

export interface ChatbotApiError {
  error: string;
  message: string;
  code?: string;
}

export type ProgressCallback = (progress: ResponseProgress) => void;

type ToolMarker = { position: number; tool: string; runId?: string; message?: string; success?: boolean | null };

const finalizeInProgressToolMarkers = (toolMarkers: ToolMarker[]): void => {
  for (let i = 0; i < toolMarkers.length; i++) {
    const marker = toolMarkers[i];
    if (marker.success === null || marker.success === undefined) {
      toolMarkers[i] = { ...marker, success: false };
    }
  }
};

const isStoppedByUserStreamInterruption = (chunk: any): boolean => {
  const err = String(chunk?.error || '').toLowerCase();
  return err === 'stopped by user' || err.includes('stopped by user');
};

// The store instance that owns `conversationId`'s UI. Multi-chat renders every
// panel chat from a per-conversation registry store, so state written here must
// target THAT instance — writing to the global `useChatbotStore` lands in a
// store nothing renders (lost interruption banners, orphaned overflow
// continuations). Registry miss → the stream belongs to a global-store surface,
// so the global store is the correct target.
const resolveConversationStore = (conversationId?: string) =>
  (conversationId && peekSharedConversationStore(conversationId)) || useChatbotStore;

const resolveStore = resolveConversationStore;

// PROVISIONAL(#7443) kill switch. ON by default — a stream that ends without
// the server's terminal event is a dropped connection under any diagnosis, and
// finalizing it as success is wrong regardless of what turns out to be causing
// the drops. Set false to keep the measurement and drop the behavior change;
// that is the one-line retreat if StreamDropped shows the drops are not real.
const RETRY_ON_DROPPED_STREAM = true;

// Why a reader was aborted. `outcome: 'aborted'` alone is four different
// events wearing one name:
//
//   user_stop   the person pressed Stop. They chose this.
//   superseded  a second reader attached and the one-live-reader contract
//               killed the first. Self-healing — the tail replays — but the
//               view resets mid-answer, which is what gets reported as
//               "Leo cut off".
//   page_unload the page was left with the reader live — tab closed, reloaded,
//               navigated off the app. Neither close path runs once the
//               document is gone, so the pagehide listener reports it (see
//               pageExitDelivery.ts). The person left; whether we had already
//               failed them is read from elapsed_ms and last_event_id, never
//               from the label.
//   unknown     nothing in this file called abort() and the page did not
//               unload, yet the reader died — the browser tearing down an
//               in-flight fetch. Navigating within the SPA does NOT land here;
//               the app stays mounted and the reader survives.
//
// Without this field the delivery rate counts every abort against us: the two
// false beacons on 2026-09-10 were both aborts with no /cancel, and the server
// had already written outcome=success for both turns 34s later.
type AbortReason = 'user_stop' | 'superseded' | 'page_unload' | 'unknown';

// `outcome: 'interrupted'` is the fourth end-state, and the only one that used
// to leave no row at all: the turn died mid-drive and the person is looking at
// a Retry button. It is NOT an abort — nothing here called abort() — so it
// carries no AbortReason; `interrupt_retryable` says whether Retry can rejoin
// the turn or the person is stuck. Retry normally REUSES the server turn id,
// so every beacon also carries the unique client request id as `attempt_id`.
// The backend keeps an immutable row per attempt while retaining the existing
// per-turn summary row for backwards-compatible delivery-rate queries.

class ChatbotApiService {
  private activeControllers = new ExecutiveResourceMap<AbortController>(controller => {
    this.abortWithReason(controller, 'superseded');
  });
  // Keyed by CONTROLLER, not by conversation — the label belongs to the reader
  // being aborted, and a conversation can hold two of them for an instant.
  // Supersede-then-Stop is the case that proves it: abort the old reader as
  // `superseded`, then Stop the new one before the old reader's catch has run.
  // Keyed by conversation the second write clobbers the first, so the old
  // reader reports `user_stop` and the new one reports `unknown` — both wrong,
  // and one of them wrong in the direction that excuses us.
  //
  // A WeakMap also fixes the leak: a cancelRequest whose AbortError catch never
  // fires (the stream had already finished) would otherwise strand `user_stop`
  // under that conversation for the NEXT abort to consume — labelling a real
  // page-death loss as a deliberate stop. Entries here die with the controller.
  private abortReasons = new WeakMap<AbortController, AbortReason>();

  // What each live reader would report if the page went away now. Keyed by
  // controller like abortReasons, and only ever read through activeControllers:
  // a reader that finished, was stopped or was superseded has left that map, so
  // its own close path is the only one that reports it. Only sendMessageStream
  // registers one — the questionnaire stream reports no close on any path.
  private inFlightDeliveries = new WeakMap<AbortController, () => InFlightDelivery & { attempt: DeliveryAttempt }>();

  constructor() {
    if (typeof window !== 'undefined') {
      // pagehide, not visibilitychange. Switching tabs hides the page while the
      // stream carries on, so a visibility beacon would file a non-delivery for
      // every backgrounded tab. pagehide fires only when the document is being
      // left. The fetch in reportStreamClosed is keepalive, which is what lets
      // it outlive the page.
      window.addEventListener('pagehide', (event) => {
        // Entering the back/forward cache is a suspension, not a final close.
        // The same reader may resume and finish, so recording page_unload here
        // would let a provisional event permanently beat its terminal result.
        if (!shouldReportPageExit(event.persisted)) return;
        for (const { controller, payload } of pageExitBeacons(
          this.activeControllers,
          this.inFlightDeliveries,
        )) {
          this.inFlightDeliveries.get(controller)?.().attempt.report(payload);
          // Script can outlive this handler — a slow unload, or a back/forward
          // cache freeze whose fetch is torn down on resume — and then the
          // reader's own abort catch runs too. The shared attempt has already
          // reported, so that catch cannot send a second receipt.
          if (!this.abortReasons.has(controller)) {
            this.abortReasons.set(controller, 'page_unload');
          }
        }
      });
    }
  }

  private abortWithReason(controller: AbortController, reason: AbortReason): void {
    this.abortReasons.set(controller, reason);
    controller.abort();
  }

  private takeAbortReason(controller: AbortController): AbortReason {
    const reason = this.abortReasons.get(controller) ?? 'unknown';
    this.abortReasons.delete(controller);
    return reason;
  }

  /**
   * Send a message to the chatbot and get a response using existing ConversationService
   */
  async sendMessage(
    message: string,
    conversationId?: string,
    onProgress?: ProgressCallback,
    uploadedImages?: any[]
  ): Promise<ChatbotApiResponse> {
    const startTime = Date.now();
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    let progressInterval: number | null = null;
    
    // Declare variables at function scope to be accessible in catch block
    let streamingResponse = '';
    let finalConversationId: string | undefined;
    let finalMetadata: any = {};

    try {
      // Start waiting stage
      onProgress?.({
        requestId,
        stage: ResponseStage.WAITING,
        progress: 25,
        currentActivity: 'Leo is thinking...',
        startTime,
        estimatedDuration: 30000,
        canCancel: true,
      });

      const messageString = buildMessageWithAttachments(message, uploadedImages);

      // Prepare request payload
      const requestPayload: CreateNewConversationRequest = {
        message: messageString,
        conversation_id: conversationId,
        enable_streaming: false,
        uploaded_images: uploadedImages?.map(im => ({
          s3_url: im.s3_url,
          asset_id: im.asset_id,
          description: im.description,
        })),
      };

      // Use NewConversationService with status code
      const { status, data } = await NewConversationService.create(requestPayload);
      if (progressInterval) clearInterval(progressInterval);
      const responseData = data as any;
      const responsePayload = responseData?.response;
      finalConversationId = responseData?.conversation_id || responsePayload?.conversation_id || conversationId;
      finalMetadata = responseData || {};

      console.log('📊 [chatbotApi.sendMessage] Final streaming state:', {
        streamingResponseLength: streamingResponse.length,
        hasResponse: !!streamingResponse?.trim(),
        responsePreview: streamingResponse?.slice(0, 100) + (streamingResponse?.length > 100 ? '...' : ''),
        finalConversationId,
        status
      });

      // Streaming response completed

      // Check for approval status in final metadata
      if (status === 202 || responsePayload?.status === 'pending_approval') {
        const r202 = responsePayload || finalMetadata;
        
        onProgress?.({
          requestId,
          stage: ResponseStage.IDLE,
          progress: 100,
          currentActivity: 'Approval required',
          startTime,
          estimatedDuration: 0,
          canCancel: false,
        });

        // Return structured content with approval data
        return {
          id: finalConversationId ?? `response_${Date.now()}`,
          content: {
            requires_approval: true,
            response: r202?.message || streamingResponse || "I need your approval to execute the following action(s):",
            approval_data: r202?.approval_data,
            request_type: 'approval',
            request_text: '',
          },
          timestamp: Date.now(),
          conversationId: finalConversationId,
          metadata: {
            requires_approval: true,
            status: 'pending_approval',
          },
        };
      }

      // Handle normal streaming response
      // We already have the complete response from streaming
      let assistantResponse = streamingResponse;

      if (!assistantResponse || assistantResponse.trim() === '') {
        console.error('❌ chatbotApi.sendMessage - No streaming response received', {
          streamingResponseLength: streamingResponse?.length || 0,
          finalMetadata,
          status
        });
        
        // Check if we have metadata with response content
        if (typeof responsePayload === 'string') {
          assistantResponse = responsePayload;
        } else if (typeof responsePayload?.message === 'string') {
          assistantResponse = responsePayload.message;
        } else if (typeof responsePayload?.response === 'string') {
          assistantResponse = responsePayload.response;
        } else if (finalMetadata?.response && typeof finalMetadata.response === 'string') {
          console.log('📋 [chatbotApi.sendMessage] Using response from finalMetadata', {
            metadataResponseLength: finalMetadata.response.length,
            metadataResponsePreview: finalMetadata.response.slice(0, 100) + '...'
          });
          assistantResponse = finalMetadata.response;
        } else {
          console.error('❌ [chatbotApi.sendMessage] No response in metadata either', {
            hasFinalMetadata: !!finalMetadata,
            finalMetadataKeys: finalMetadata ? Object.keys(finalMetadata) : [],
            finalMetadataResponse: finalMetadata?.response,
            finalMetadataResponseType: typeof finalMetadata?.response
          });
          throw new Error('No assistant response received from streaming request');
        }
      }

      onProgress?.({
        requestId,
        stage: ResponseStage.IDLE,
        progress: 100,
        currentActivity: 'Response complete',
        startTime,
        estimatedDuration: 0,
        canCancel: false,
      });

      return {
        id: finalConversationId ?? `response_${Date.now()}`,
        content: assistantResponse,
        timestamp: Date.now(),
        conversationId: finalConversationId,
        metadata: {
          requires_approval: finalMetadata?.requires_approval,
          ...finalMetadata,
        },
      };
    } catch (error) {
      // Make sure to clear the progress interval on error
      if (progressInterval) clearInterval(progressInterval);
      
      console.error('❌ [chatbotApi.sendMessage] Error caught:', {
        error,
        errorType: error?.constructor?.name,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        streamingResponseLength: streamingResponse?.length || 0,
        finalConversationId,
        timestamp: new Date().toISOString()
      });
      
      // Provide better error context
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Request cancelled');
        }
        throw error;
      }
      throw new Error('Unknown error occurred');
    }
  }

  /**
   * True when this client already holds a live reader (send or attach) for the
   * conversation. The lifecycle sync uses this as its single-flight guard so a
   * mount/switch/reload never stacks a second SSE reader on a chat that is
   * already streaming in this tab.
   */
  /**
   * Rebuild the conversation from persisted state after a stream that reached
   * its terminal event but rendered nothing.
   *
   * The server finished and saved the answer; this reader simply never received
   * it. Reloading from the saved conversation is what a page refresh would have
   * done, without the refresh — and it needs no guess about which turn the
   * answer belongs to, because `mergeSnapshotWithLiveTail` lets the snapshot win
   * whenever it holds a reply after the last user message. If the turn truly
   * produced nothing, the snapshot shows that truth instead of an expanded
   * reasoning pill standing in for a reply.
   *
   * Deferred so the send that owns this stream settles first, and skipped when
   * the person has moved to another conversation or a newer reader has started.
   */
  private rehydrateAfterEmptyClose(conversationId: string, receipt?: RehydrateReceipt): void {
    const sessionCurrent = captureExecutiveSession(conversationId);
    setTimeout(() => {
      void (async () => {
        const startedAt = Date.now();
        try {
          if (!sessionCurrent()) return;
          const store = resolveStore(conversationId).getState();
          if (store.conversationId !== conversationId || this.hasActiveStream(conversationId)) {
            return;
          }
          await store.loadConversationById(conversationId);
        } catch {
          /* recovery is best-effort; the beacon already recorded the miss */
        }
        if (receipt && sessionCurrent()) this.fileRehydrateReceipt(conversationId, receipt, startedAt);
      })();
    }, 0);
  }

  /**
   * Say whether loading the saved conversation recovered the answer.
   *
   * The attempt that triggered the rehydrate already recorded the stream's
   * failure, and that row stays. This is a second, later attempt under the
   * same turn and message: painted, the person got their reply after all;
   * empty, they did not. Without it every rescue counted as a miss, and the
   * rate could not tell a recovered answer from a lost one.
   *
   * Skipped when the person has moved on — what is on screen then belongs to
   * another conversation and says nothing about this one.
   */
  private fileRehydrateReceipt(conversationId: string, receipt: RehydrateReceipt, startedAt: number): void {
    try {
      const store = resolveStore(conversationId).getState();
      if (store.conversationId !== conversationId || this.hasActiveStream(conversationId)) return;
      const loadFailed = store.error?.code === 'CONVERSATION_LOAD_ERROR';
      const replyChars = loadFailed ? null : savedReplyChars(store.messages);
      const attemptId = mintDeliveryId('req', startedAt);
      reportStreamClosed(conversationId, {
        ...deliveryBeaconIdentity(receipt.turnId, attemptId, startedAt),
        send_id: receipt.sendId,
        outcome: 'terminal',
        transport: 'rehydrate',
        recovery: 'rehydrate',
        answer_rendered: replyChars !== null,
        answer_chars: replyChars ?? 0,
        last_event_id: 0,
        elapsed_ms: Math.max(0, Date.now() - startedAt),
      }, receipt.headers);
      if (!loadFailed && replyChars === null) receipt.onEmpty?.();
    } catch {
      /* a receipt must never break the page it is describing */
    }
  }

  hasActiveStream(conversationId: string): boolean {
    return this.activeControllers.has(executiveResourceKey(conversationId));
  }

  /**
   * Attach to an in-flight worker turn WITHOUT sending anything: a read-only
   * tail of the turn's durable Redis stream via GET /subscribe, replayed from
   * seq 0 so the partial already streamed is fully reconstructed. This is the
   * reattach primitive for mounts/reloads/chat-switches — unlike a POST /stream
   * resume it can never enqueue a turn, so a status-vs-attach race degrades to
   * an empty tail instead of a ghost empty-message turn.
   */
  async attachStream(
    conversationId: string,
    onProgress?: ProgressCallback,
    turnId?: string,
  ): Promise<ChatbotApiResponse> {
    return this.sendMessageStream(
      '',
      conversationId,
      undefined,
      true, // isRetry — reattach must never render a new user message
      undefined,
      undefined,
      undefined,
      onProgress,
      undefined,
      undefined,
      undefined,
      undefined,
      { transport: 'subscribe', turnId },
    );
  }

  /**
   * Send message with streaming (SSE)
   * Supports SSE reconnection via Last-Event-ID header for resilient streaming.
   */
  async sendMessageStream(...args: Parameters<ChatbotApiService['sendPreparedMessageStream']>): Promise<ChatbotApiResponse> {
    const attempt = args[12]?.attempt ?? createDeliveryAttempt(args[1]);
    try {
      attempt.ensureHeaders();
      attempt.phase = 'stream_preparation';
      args[12] = { ...args[12], attempt };
      return await this.sendPreparedMessageStream(...args);
    } catch (error) {
      attempt.fail(error);
      throw error;
    }
  }

  private async sendPreparedMessageStream(
    message: string,
    conversationId: string,
    uploadedImages?: any[],
    isRetry?: boolean,
    lastEventIdOverride?: number,
    initialContent?: string,
    isHidden?: boolean,
    onProgress?: ProgressCallback,
    displayText?: string,
    slackSimulation?: { enabled: boolean; async?: boolean },
    agentArchitecture?: 'classic' | 'code' | 'code_simple',
    askCard?: boolean,
    opts?: { transport?: 'post' | 'subscribe'; attempt?: DeliveryAttempt; turnId?: string },
  ): Promise<ChatbotApiResponse> {
      const resourceKey = executiveResourceKey(conversationId);
      const sessionCurrent = captureExecutiveSession(conversationId);
      const owningStore = resolveConversationStore(conversationId);
      const progress = onProgress;
      onProgress = update => { if (sessionCurrent()) progress?.(update); };
      const resolveStore = (id?: string) => conversationId.startsWith('exec__')
        ? owningStore : resolveConversationStore(id);
      const attempt = opts!.attempt!;
      const { startTime, requestId } = attempt;
      const deliveryHeaders = attempt.headers!;
      let fullText = initialContent || '';
      // Node-gated answer isolation. Each streamed token is tagged with its
      // LangGraph node; only `conclusion_render` is user-facing. We accumulate
      // that segment separately so the UI never shows a draft reply a weak
      // auxiliary model (Gemini Flash) may emit in an earlier step (e.g. triage).
      // Mirrors the Slack delivery path (slack_agent_task.py gates on
      // node === 'conclusion_render'). `nodeTagged` flips true on the first
      // node-bearing token, telling the UI the stream is node-aware.
      let conclusionText = initialContent || '';
      // Where the answer the model is writing now began in `fullText`, so a
      // `reply_reset` takes back that answer and nothing before it.
      let answerStart: number | null = null;
      // The harness's word on the turn (`statusActivityNote`), the pill's
      // activity line until the next step starts.
      let activityNote: string | undefined;
      let nodeTagged = false;
      let outputTokenCount = 0; // Track output tokens (from streaming)
      let baselineInputTokens = 0; // System prompt + tools + user message + history
      let metaToolsTokens = 0; // Meta tools when loaded
      let toolResponseTokens = 0; // Cumulative tool response tokens
      const activeTools = new Set<string>();
      const toolMarkers: Array<{ position: number; tool: string; runId?: string; message?: string; success?: boolean | null }> = [];
      let toolsNodeCompleted = false;
      let finalResponseStarted = false;

      // Track last event ID for SSE reconnection support
      // If connection drops, this can be used to resume from the last received event
      let lastEventId = lastEventIdOverride || 0;

      // The server already stamps `turn_id` on every published event — both
      // producers do: `agent_task.publish_event` for an ordinary chat turn (the
      // path this client is on) and `StreamPublisher` in redis_stream.py for the
      // async/unified-interrupts path. Capturing it is what makes the
      // browser's account of a turn joinable to the server's per TURN —
      // conversation_id alone cannot do it, because one conversation holds many
      // turns and one turn can produce several stream exits (the API re-enters
      // stream_from_redis on WorkerLostError).
      //
      // Declared out here, beside lastEventId, for the same two reasons it is:
      // it belongs to the TURN, not to one attempt, so a silent reconnect must
      // not forget it — and the abort path reports from a `catch`, which cannot
      // see a `let` declared inside the `try` it is catching.
      //
      // NOTE this is the STREAM turn_id (content hash + nonce,
      // stream_lifecycle.build_turn_id), NOT the LeoTurnSummary ledger key
      // `{conversation_id}#{user_message_idx}`. Same name, different identifier.
      // It joins to StreamExit; it does not join to the cost ledger.
      let streamTurnId: string | null = null;

      // One reader, at most one beacon. Every close path in this function sets
      // this, so the catch-all at the bottom can report the paths that have no
      // reporter of their own WITHOUT double-counting the ones that do — the
      // dropped-stream throw has already written its row by the time it lands
      // there, and a second row under the same key would overwrite a real
      // `dropped` with a vaguer `interrupted`.

      // A recovery budget starts at the first failure, not at the last event.
      // Healthy worker streams can be quiet for 30s between keepalives.
      let reconnectStartedAt: number | null = null;
      let lastStreamEventAt = startTime;
      let reconnectAttempts = 0;
      let turnChangedRetries = 0;
      let totalReconnectAttempts = 0;
      let stallReconnects = 0;
      let hadNetworkDisconnect = false;
      let readOnlyRecovery = opts?.transport === 'subscribe' || lastEventId > 0;
      // A 502 on the POST itself may have come after the server took the
      // message. The existing policy resends it once on server idempotency;
      // a park across a reload must not assume the same.
      let postMayHaveLanded = false;
      let clientErrorKind: 'network' | 'http' | 'stall' | 'worker_lost' | 'client' | 'unknown' | undefined;
      let clientErrorStatus: number | undefined;
      let lastErrorEventAgeMs: number | undefined;
      let recoveryStopReason: 'attempt_limit' | 'time_limit' | 'aborted' | 'non_retryable' | undefined;
      const recoveryDiagnostics = () => ({
        client_error_kind: clientErrorKind,
        client_error_status: clientErrorStatus,
        recovery_stop_reason: recoveryStopReason,
        total_reconnect_attempts: totalReconnectAttempts,
        stall_reconnects: stallReconnects,
        last_event_age_ms: lastErrorEventAgeMs,
      });
      const rehydrateReceipt = (onEmpty?: () => void): RehydrateReceipt => ({
        turnId: streamTurnId ?? opts?.turnId ?? null,
        sendId: attempt.sendId,
        headers: deliveryHeaders,
        onEmpty,
      });

      // "Did this turn put a reply on the screen" — defined ONCE, because both
      // close paths report it (the normal close and the abort `catch`) and two
      // copies of a metric's definition drift silently: the next person to
      // change the node-gating rule fixes the copy they are looking at and the
      // number quietly starts meaning two things.
      //
      // Functions, not values: both buffers fill as the stream runs, so this has
      // to be read at close time. `nodeTagged ? conclusionText : fullText`
      // mirrors the UI's own gating — only `conclusion_render` tokens are
      // user-facing, and a stream that never tagged a node has no conclusion
      // segment to gate on.
      const renderedAnswerText = () => (nodeTagged ? conclusionText : fullText);
      const didRenderAnswer = () => renderedAnswerText().trim().length > 0;

      // Leaky-bucket drip: server chunks arrive bursty (variable-size deltas,
      // sometimes hundreds of chars at once). Buffer + drain at a ~1s target
      // so text appears typewriter-style instead of jumping in paragraph-sized
      // batches. Non-token events flush the buffers first so tool markers land
      // after the text they belong to; graph_complete is deferred until the
      // buffers drain so the reply's last chars aren't dumped in a terminal
      // flush. Two buffers because two fields drive the UI: conclusionContent
      // (the visible reply under node-gating) and streamedContent (fallback).
      const DRIP_MIN_CHARS_PER_FRAME = 1;
      const DRIP_TARGET_DRAIN_FRAMES = 60; // ~1s at 60fps
      const progressCallback = onProgress;
      const rawOnProgress = progressCallback
        ? (progress: ResponseProgress) => progressCallback({ ...progress, turnId: streamTurnId ?? opts?.turnId })
        : undefined;
      let streamedBuffer = '';
      let streamedDisplayedLen = 0;
      let conclusionBuffer = '';
      let conclusionDisplayedLen = 0;
      let latestTokenPayload: ResponseProgress | null = null;
      let dripRaf: number | null = null;
      // graph_complete defers here so the drip can drain the last-arrived
      // buffer instead of the whole reply landing at once via the terminal
      // payload.
      let pendingTerminalPayload: ResponseProgress | null = null;
      const cancelDripRaf = () => {
        if (dripRaf != null) {
          cancelAnimationFrame(dripRaf);
          dripRaf = null;
        }
      };
      // Back up to the last position where every '<' has a matching '>' so
      // we never emit mid-tag. Streamed content includes <artifact>, <action>,
      // <attachment>, <attachments>, <conclusion> tags; a mid-tag prefix
      // renders as literal partial XML (removeAttachmentsTags won't match an
      // unclosed opener). '<' only counts as a tag-open when followed by
      // letter or '/' to avoid stalling on comparison operators like `x < y`.
      const safeTagBoundary = (text: string, upTo: number): number => {
        if (upTo <= 0) return 0;
        const end = Math.min(upTo, text.length);
        let depth = 0;
        let safe = 0;
        for (let i = 0; i < end; i++) {
          const c = text[i];
          if (c === '<') {
            const next = text[i + 1];
            if (next === '/' || (next && ((next >= 'a' && next <= 'z') || (next >= 'A' && next <= 'Z')))) {
              depth++;
            }
          } else if (c === '>' && depth > 0) {
            depth--;
          }
          if (depth === 0) safe = i + 1;
        }
        return safe;
      };
      const emitDripped = () => {
        if (!rawOnProgress || !latestTokenPayload) return;
        const payload = latestTokenPayload;
        const streamedText = payload.streamedContent || '';
        const streamedShown = safeTagBoundary(streamedText, streamedDisplayedLen);
        const hasConclusion = payload.conclusionContent !== undefined;
        const conclusionText = payload.conclusionContent || '';
        const conclusionShown = hasConclusion
          ? safeTagBoundary(conclusionText, conclusionDisplayedLen)
          : 0;
        rawOnProgress({
          ...payload,
          streamedContent: streamedText.slice(0, streamedShown),
          conclusionContent: hasConclusion
            ? conclusionText.slice(0, conclusionShown)
            : payload.conclusionContent,
        });
      };
      const drainOne = (buffer: string, into: (n: number) => void): string => {
        if (buffer.length === 0) return buffer;
        const take = Math.max(
          DRIP_MIN_CHARS_PER_FRAME,
          Math.ceil(buffer.length / DRIP_TARGET_DRAIN_FRAMES),
        );
        const sliceLen = Math.min(take, buffer.length);
        into(sliceLen);
        return buffer.slice(sliceLen);
      };
      const firePendingTerminal = () => {
        if (!pendingTerminalPayload || !rawOnProgress) return;
        const terminal = pendingTerminalPayload;
        pendingTerminalPayload = null;
        rawOnProgress(terminal);
      };
      const dripStep = () => {
        dripRaf = null;
        const hadContent = streamedBuffer.length > 0 || conclusionBuffer.length > 0;
        if (!hadContent) {
          firePendingTerminal();
          return;
        }
        streamedBuffer = drainOne(streamedBuffer, n => { streamedDisplayedLen += n; });
        conclusionBuffer = drainOne(conclusionBuffer, n => { conclusionDisplayedLen += n; });
        // Emit even when the drain just emptied the buffers — the chars just
        // added to displayedLen still need to reach the store.
        emitDripped();
        if (streamedBuffer.length > 0 || conclusionBuffer.length > 0) {
          dripRaf = requestAnimationFrame(dripStep);
        } else {
          firePendingTerminal();
        }
      };
      const scheduleTokenProgress = (payload: ResponseProgress) => {
        if (!rawOnProgress) return;
        latestTokenPayload = payload;
        const serverText = payload.streamedContent || '';
        const serverConclusion = payload.conclusionContent || '';
        const streamedQueued = streamedDisplayedLen + streamedBuffer.length;
        if (serverText.length > streamedQueued) {
          streamedBuffer += serverText.slice(streamedQueued);
        }
        const conclusionQueued = conclusionDisplayedLen + conclusionBuffer.length;
        if (serverConclusion.length > conclusionQueued) {
          conclusionBuffer += serverConclusion.slice(conclusionQueued);
        }
        if (
          dripRaf == null &&
          (streamedBuffer.length > 0 || conclusionBuffer.length > 0)
        ) {
          dripRaf = requestAnimationFrame(dripStep);
        }
      };
      // graph_complete calls this instead of onProgress so the terminal
      // payload waits for the drip to drain. If the buffers are already
      // empty we fire immediately.
      const scheduleTerminal = (p: ResponseProgress) => {
        if (!rawOnProgress) return;
        if (streamedBuffer.length === 0 && conclusionBuffer.length === 0) {
          rawOnProgress(p);
          return;
        }
        pendingTerminalPayload = p;
        if (dripRaf == null) {
          dripRaf = requestAnimationFrame(dripStep);
        }
      };
      const flushDrip = () => {
        cancelDripRaf();
        if (streamedBuffer.length > 0) {
          streamedDisplayedLen += streamedBuffer.length;
          streamedBuffer = '';
        }
        if (conclusionBuffer.length > 0) {
          conclusionDisplayedLen += conclusionBuffer.length;
          conclusionBuffer = '';
        }
        // A wrapper-triggered flush overrides any deferred terminal — an abort
        // / error / late capacity_info takes precedence over graph_complete's
        // pending payload.
        pendingTerminalPayload = null;
      };
      onProgress = rawOnProgress
        ? (p: ResponseProgress) => {
            flushDrip();
            // Non-token emits are authoritative — advance displayedLen so the
            // next token's drip doesn't slice to a shorter prefix and rewind
            // the store. Matters on stream resume (initialContent lands before
            // any tokens).
            if (
              typeof p.streamedContent === 'string' &&
              p.streamedContent.length > streamedDisplayedLen
            ) {
              streamedDisplayedLen = p.streamedContent.length;
            }
            if (
              typeof p.conclusionContent === 'string' &&
              p.conclusionContent.length > conclusionDisplayedLen
            ) {
              conclusionDisplayedLen = p.conclusionContent.length;
            }
            rawOnProgress(p);
          }
        : undefined;

      // Create AbortController for cancellation support per conversation.
      // A conversation gets ONE live reader: superseding an existing one aborts
      // it first, so an overwrite never orphans an unabortable stream whose
      // events would interleave with the new reader's.
      const supersededController = this.activeControllers.get(resourceKey);
      if (supersededController) {
        console.warn(`[chatbotApi] Superseding active stream for conversationId: ${conversationId}`);
        this.abortWithReason(supersededController, 'superseded');
      }
      console.log(`[chatbotApi] Stream registered for conversationId: ${conversationId} (active streams: ${this.activeControllers.size + 1})`);
      const streamController = new AbortController();
      this.activeControllers.set(resourceKey, streamController);
      resolveStore(conversationId).getState().updateSSEConnectionState({ turnId: opts?.turnId });
      this.inFlightDeliveries.set(streamController, () => ({
        attempt,
        turnId: streamTurnId,
        requestId,
        attemptStartedAtMs: startTime,
        requestHeaders: deliveryHeaders,
        answerRendered: didRenderAnswer(),
        answerChars: renderedAnswerText().length,
        lastEventId,
        elapsedMs: Date.now() - startTime,
      }));
      // Delete only our own registration — if a newer stream superseded this
      // one, its controller must survive our cleanup.
      const releaseController = () => {
        if (this.activeControllers.get(resourceKey) === streamController) {
          this.activeControllers.delete(resourceKey);
        }
      };

    try {
      onProgress?.({
        requestId,
        stage: ResponseStage.WAITING,
        progress: 25,
        currentActivity: 'Starting stream...',
        startTime,
        estimatedDuration: 30000,
        canCancel: true,
      });
      
      if (fullText) {
        onProgress?.({
          requestId,
          stage: ResponseStage.WAITING,
          progress: 25,
          currentActivity: 'Resuming...',
          startTime,
          estimatedDuration: 30000,
          canCancel: true,
          streamedContent: fullText,
        });
      }


      // Format message with images if provided (same format as sendMessage)
      const messageString = buildMessageWithAttachments(message, uploadedImages);

      // SSE reconnection configuration
      const MAX_RECONNECT_WINDOW_MS = 15000; // Silent retry window for brief disconnects
      const MAX_RECONNECT_ATTEMPTS = 10;
      const RECONNECT_DELAY_MS = 1000;
      // Keep a lifetime bound even when a flapping connection sends one event
      // between failures and resets its consecutive-failure budget.
      const MAX_TOTAL_RECONNECTS = 20;
      // Stalls are counted separately and capped low: each one costs the full
      // SSE_STALL_TIMEOUT_MS before it is even detected.
      const MAX_STALL_RECONNECTS = 2;
      let streamComplete = false;
      // Heartbeat loss is not a dead turn. Follow it while the server still
      // says the turn is running, instead of ending on a non-retryable banner.
      let workerLossReattaches = 0;
      let workerLossStartedAt: number | null = null;
      
      // Reconnection loop - will retry on network errors
      while (!streamComplete && reconnectAttempts <= MAX_RECONNECT_ATTEMPTS) {
        if (!sessionCurrent()) throw new DOMException('Executive session changed', 'AbortError');
        try {
          streamController.signal.throwIfAborted();
          // Did the SERVER say this turn was over? `graph_complete` sets it;
          // the branches that end a turn any other way (structured_questions,
          // continuation_complete, stream_interrupted) return out of this
          // function, and `error` throws. So reaching the end of the loop with
          // this still false means the body closed on us mid-turn.
          let sawTerminalEvent = false;
          // Paired with the above so an EMPTY body stays benign: the read-only
          // `subscribe` tail legitimately closes with nothing to replay, and
          // that must not be mistaken for a drop. Keepalives count as data —
          // a stream that only ever kept alive and then closed IS a drop.
          let sawAnyEvent = false;

          // Stream using NewConversationService
          // The service now yields { eventId, data } for SSE reconnection support
          // On reconnection, lastEventId tells the backend to skip already-received events.
          // The 'subscribe' transport (attachStream) tails the same event stream
          // read-only via GET /subscribe — identical event shape, no enqueue path.
          const requestContext = {
            headers: deliveryHeaders,
            onRequest: () => { attempt.phase = 'transport'; },
            sendId: attempt.sendId,
          };
          const eventSource = readOnlyRecovery
            ? NewConversationService.subscribeStream(
                conversationId,
                lastEventId > 0 ? lastEventId : undefined,  // Only send on reconnection
                streamController.signal,
                requestContext,
              )
            : NewConversationService.streamMessage(
                conversationId,
                messageString,
                uploadedImages,
                isRetry,
                lastEventId > 0 ? lastEventId : undefined,  // Only send on reconnection
                isHidden,
                streamController.signal,
                displayText,
                slackSimulation,
                agentArchitecture,
                askCard,
                requestContext,
              );
          for await (const { eventId, data: chunk } of eventSource) {
            if (!sessionCurrent()) throw new DOMException('Executive session changed', 'AbortError');
            lastStreamEventAt = Date.now();
            reconnectStartedAt = null;
            // Once the server has acknowledged the stream, subsequent reads
            // only tail it. A reconnect must never submit the user's work again.
            readOnlyRecovery = true;
            sawAnyEvent = true;
            // Every published event carries it, so the first one wins and the
            // rest are free. Read structurally rather than widening the chunk
            // union: this is one telemetry field, not a new event contract.
            if (!streamTurnId) {
              const t = (chunk as unknown as { turn_id?: unknown })?.turn_id;
              if (typeof t === 'string' && t) {
                streamTurnId = t;
                resolveStore(conversationId).getState().updateSSEConnectionState({ turnId: t });
              }
            }

            // Track the last event ID for potential reconnection
            if (eventId > lastEventId) {
              lastEventId = eventId;
              const store = resolveStore(conversationId).getState();
              if (store.conversationId === conversationId) {
                store.updateSSEConnectionState({ lastEventId });
              }
            }

            // Reset reconnect attempts on successful data receipt
            reconnectAttempts = 0;
            if (hadNetworkDisconnect) {
              const store = resolveStore(conversationId).getState();
              if (store.conversationId === conversationId) {
                store.handleSSEReconnect();
              }
              hadNetworkDisconnect = false;
            }
            

        // An executive steer's ack (apis/executiveContract.ts): queued or landed in the running turn.
        const steerAck = parseSteerAck(chunk, Date.now());
        if (steerAck) {
          resolveStore(conversationId).setState({ executiveSteer: steerAck, executiveNotice: null });
          continue;
        }
        if (chunk.type === 'keepalive') {
          // Keepalive comments are normal heartbeats; avoid false "reconnecting" UI.
          continue;
        } else if (chunk.type === 'capacity_info') {
          // Update capacity from backend (single source of truth)
          // Backend sends one capacity_info event per exchange, after messages are saved to S3
          const tokenCount = chunk.token_count || 0;
          const maxTokens = chunk.max_tokens || 200000;
          const capacityPercentage = chunk.capacity_percentage || 0;
          
          onProgress?.({
            requestId,
            stage: ResponseStage.IDLE,  // Capacity arrives after save, so we're done
            progress: 100,
            currentActivity: 'Complete',
            startTime,
            estimatedDuration: 0,
            canCancel: false,
            streamedContent: fullText,
            conclusionContent: nodeTagged ? conclusionText : undefined,
            nodeTagged,
            toolMarkers: [...toolMarkers],
            activeTools: Array.from(activeTools),
            messageCapacity: {
              tokenCount,
              capacityPercentage,
              maxTokens,
              isAtLimit: capacityPercentage >= 100
            }
          } as any);
        } else if (chunk.type === 'token_baseline') {
          // Capture baseline input tokens (system prompt, tools, user message, history)
          baselineInputTokens = chunk.input_tokens || 0;
        } else if (chunk.type === 'meta_tools_loaded') {
          // Add Meta tools tokens when platform is activated
          metaToolsTokens = chunk.meta_tools_tokens || 0;
        } else if (chunk.type === 'token') {
          const tokenAt = fullText.length;
          fullText += chunk.content || '';
          // A model that writes is not quiet: the harness's last word gives way.
          activityNote = undefined;
          // Isolate the render-node segment (the user-facing answer). A token
          // tagged with any node means the stream is node-aware; only the
          // conclusion_render node's text is the reply body. Code-mode's
          // `code_model` tokens are the model's thinking narration (reasoning
          // summaries + working text) — they belong to the WorkingSection via
          // streamedContent, NOT the answer body. A code-mode turn that never
          // opens a <conclusion> wrapper (so nothing ever streams as
          // conclusion_render) is handled by the consumers' completion
          // fallback, which applies the full text once the turn ends.
          if (typeof chunk.node === 'string' && chunk.node) {
            nodeTagged = true;
            if (chunk.node === 'conclusion_render') {
              if (answerStart === null) answerStart = tokenAt;
              conclusionText += chunk.content || '';
            }
          }
          outputTokenCount++;

          // If tokens are streaming after tools completed, mark incomplete tools as complete
          if (toolsNodeCompleted && !finalResponseStarted) {
            finalResponseStarted = true;
            toolMarkers.forEach((marker, index) => {
              if (!marker.message) {
                toolMarkers[index] = { ...marker, message: `using ${marker.tool}`, success: true };
              } else if (marker.success === null || marker.success === undefined) {
                toolMarkers[index] = { ...marker, success: true };
              }
            });
          }
          
          // Calculate total tokens (with better approximation)
          // Output tokens: use 1.5x coefficient since chunks aren't exactly tokens
          // Input tokens: baseline + meta tools + tool responses
          const estimatedOutputTokens = Math.round(outputTokenCount * 1.5);
          const totalTokens = baselineInputTokens + metaToolsTokens + toolResponseTokens + estimatedOutputTokens;

          scheduleTokenProgress({
            requestId,
            stage: ResponseStage.STREAMING,
            progress: 75,
            currentActivity: `Streaming... (${totalTokens.toLocaleString()} tokens)`,
            startTime,
            estimatedDuration: 30000,
            canCancel: true,
            streamedContent: fullText,
            conclusionContent: nodeTagged ? conclusionText : undefined,
            nodeTagged,
            activityNote,
            toolMarkers: [...toolMarkers],
          });
        } else if (chunk.type === 'tool_start') {
          const toolName = chunk.tool || 'unknown';
          const toolMessage = chunk.message || `using ${toolName}`;
          const runId = chunk.run_id;
          const toolInput = chunk.input || {};
          activeTools.add(toolName);
          addToolBreadcrumb(toolName, 'start', { runId });
          // A new step is the activity now; the harness's last word gives way.
          activityNote = undefined;
          
          // Track tool inputs for use in tool_end events
          if (
            runId &&
            (toolName === 'update_draft' ||
              toolName === 'publish_report' ||
              toolName === 'set_report_metadata' ||
              toolName === 'update_report' || // legacy, kept for in-flight conversations
              toolName === 'create_report' ||
              toolName === 'update_point_explanation')
          ) {
            (window as unknown as Record<string, unknown>).__pendingToolInputs =
              (window as unknown as Record<string, Record<string, unknown>>).__pendingToolInputs || {};
            (window as unknown as Record<string, Record<string, unknown>>).__pendingToolInputs[runId] = toolInput;
          }
          
          // Record tool marker at current text position with message
          // Use position in text, or 0 if no text yet (tools called before response)
          const position = fullText.length;
          const marker = { position, tool: toolName, runId, message: toolMessage, success: null }; // null = in progress
          
          // Handle tool marker creation/update based on runId
          if (runId) {
            // If runId provided, check for existing marker to update (e.g., video status progression)
            const existingIndex = toolMarkers.findIndex(m => m.runId === runId);
            if (existingIndex !== -1) {
              // Update existing marker's message for progressive status updates
              toolMarkers[existingIndex] = { ...toolMarkers[existingIndex], message: toolMessage };
            } else {
              // New marker with this runId
              toolMarkers.push(marker);
            }
          } else {
            // No runId - use position+tool+message for deduplication (legacy behavior)
            const exists = toolMarkers.some(m => m.position === position && m.tool === toolName && m.message === toolMessage);
            if (!exists) {
              toolMarkers.push(marker);
            }
          }
          
          onProgress?.({
            requestId,
            stage: ResponseStage.WAITING,
            progress: 60,
            currentActivity: `Using tool: ${toolName}`,
            startTime,
            estimatedDuration: 30000,
            canCancel: true,
            activeTools: Array.from(activeTools),
            toolMarkers: [...toolMarkers],
            streamedContent: fullText,
            activityNote,
          });
        } else if (chunk.type === 'tool_end') {
          const toolName = chunk.tool || 'unknown';
          const toolMessage = chunk.message || `Executed ${toolName}`;
          // Set success: true if explicitly true, false if explicitly false, otherwise null (in progress)
          const toolSuccess = chunk.success === true ? true : chunk.success === false ? false : null;
          const runId = chunk.run_id;
          const executionTodo = extractExecutionTodoFromStreamChunk(chunk);
          activeTools.delete(toolName);
          addToolBreadcrumb(toolName, 'end', { runId, success: toolSuccess });

          if (executionTodo) {
            const store = resolveStore(conversationId).getState();
            if (store.conversationId === conversationId) {
              store.setExecutionTodo(executionTodo);
            }
          }
          
          // Emit custom event when report is updated/created/published so Leo Reports page can auto-refresh
          if (
            (toolName === 'update_draft' ||
              toolName === 'publish_report' ||
              toolName === 'set_report_metadata' ||
              toolName === 'update_report' || // legacy
              toolName === 'create_report') &&
            toolSuccess !== false
          ) {
            // Try to get report_id from cached tool input or parse from message
            let reportId: string | undefined;
            const pendingInputs = (window as unknown as Record<string, Record<string, Record<string, unknown>>>).__pendingToolInputs;

            // For update_draft / publish_report etc., get report_id from cached input
            if (runId && pendingInputs?.[runId]) {
              reportId = pendingInputs[runId]?.report_id as string | undefined;
              delete pendingInputs[runId];
            }
            
            // For create_report (or if report_id wasn't in input), parse from success message
            if (!reportId && toolMessage) {
              const match = toolMessage.match(/report_\d+_[a-f0-9]+/);
              if (match) {
                reportId = match[0];
              }
            }
            
            console.log(`📊 SSE: ${toolName} completed, reportId=${reportId}`);
            window.dispatchEvent(new CustomEvent('leo-report-updated', { 
              detail: { reportId, action: toolName === 'create_report' ? 'created' : 'updated' }
            }));
          }
          
          // Emit custom event when strategic notes are updated so My Company page can auto-refresh
          if (toolName === 'update_strategic_notes' && toolSuccess !== false) {
            window.dispatchEvent(new CustomEvent('leo-strategic-notes-updated'));
          }

          // Emit rundown point event so the chart can highlight + typewriter the explanation
          if (toolName === 'update_point_explanation' && toolSuccess !== false) {
            const pendingRundownInputs = (window as unknown as Record<string, Record<string, Record<string, unknown>>>).__pendingToolInputs;
            if (runId && pendingRundownInputs?.[runId]) {
              const input = pendingRundownInputs[runId];
              window.dispatchEvent(new CustomEvent('leo-rundown-point', {
                detail: {
                  objectiveId: input.objective_id,
                  date: input.date,
                  narrative: input.explanation,
                },
              }));
              delete pendingRundownInputs[runId];
            }
          }
          
          // Add tool response tokens to cumulative total
          if (chunk.response_tokens) {
            toolResponseTokens += chunk.response_tokens;
          }
          
          // Update tool marker with message - match by runId if available
          let markerIndex = -1;
          if (runId) {
            // Prefer matching by runId for precise identification
            markerIndex = toolMarkers.findIndex(m => m.runId === runId);
          } else {
            // Fallback: find the most recent incomplete one for this tool
            // Look for markers with null success status (in progress)
            for (let i = toolMarkers.length - 1; i >= 0; i--) {
              if (toolMarkers[i].tool === toolName && (toolMarkers[i].success === null || toolMarkers[i].success === undefined)) {
                markerIndex = i;
                break;
              }
            }
          }
          
          if (markerIndex !== -1) {
            toolMarkers[markerIndex] = { 
              ...toolMarkers[markerIndex], 
              message: toolMessage,
              success: toolSuccess
            };
          } else {
            // If no matching marker found, add a new one at current position
            const position = fullText.length;
            toolMarkers.push({ position, tool: toolName, runId, message: toolMessage, success: toolSuccess });
          }
          
          onProgress?.({
            requestId,
            stage: ResponseStage.WAITING,
            progress: 70,
            currentActivity: `Completed: ${toolName}`,
            startTime,
            estimatedDuration: 30000,
            canCancel: true,
            activeTools: Array.from(activeTools),
            toolMarkers: [...toolMarkers],
            streamedContent: fullText,
            executionTodo: executionTodo || undefined,
          });
        } else if (chunk.type === 'tools_node_end') {
          toolsNodeCompleted = true;
          // Mark all incomplete tools as complete when tools node ends
          toolMarkers.forEach((marker, index) => {
            if (!marker.message) {
              toolMarkers[index] = { ...marker, message: `using ${marker.tool}`, success: true };
            } else if (marker.success === null || marker.success === undefined) {
              // Catch synthetic markers (e.g. video_processing) that have a message but no success
              toolMarkers[index] = { ...marker, success: true };
            }
          });
          onProgress?.({
            requestId,
            stage: ResponseStage.WAITING,
            progress: 70,
            currentActivity: 'Tools completed',
            startTime,
            estimatedDuration: 30000,
            canCancel: true,
            activeTools: Array.from(activeTools),
            toolMarkers: [...toolMarkers],
            streamedContent: fullText,
          });
        } else if (chunk.type === 'agent_cycle_start') {
          const description = chunk.description || '';
          // If this is the final response after tools, mark incomplete tools as complete
          if (description.includes('final response') && toolsNodeCompleted) {
            finalResponseStarted = true;
            toolMarkers.forEach((marker, index) => {
              if (!marker.message) {
                toolMarkers[index] = { ...marker, message: `using ${marker.tool}`, success: true };
              } else if (marker.success === null || marker.success === undefined) {
                toolMarkers[index] = { ...marker, success: true };
              }
            });
            onProgress?.({
              requestId,
              stage: ResponseStage.STREAMING,
              progress: 75,
              currentActivity: 'Final response starting',
              startTime,
              estimatedDuration: 30000,
              canCancel: true,
              activeTools: Array.from(activeTools),
              toolMarkers: [...toolMarkers],
              streamedContent: fullText,
            });
          }
        } else if (chunk.type === 'structured_questions') {
          // Agent paused to present a structured questionnaire.
          // Emit as final response metadata so the store can render the
          // questionnaire wizard and pause waiting for user answers.
          console.log('[chatbotApi] Structured questions received:', chunk.questions?.length, 'questions');
          // Mark all in-progress tools as complete (the interrupt pauses before tool_end fires)
          toolMarkers.forEach((marker, index) => {
            if (marker.success === null || marker.success === undefined) {
              toolMarkers[index] = { ...marker, success: true };
            }
          });
          // The questionnaire is Leo's reply to this turn. It returns before the
          // stream's graph_complete, so it reports and lets go here; a reader left
          // registered filed page_unload when the person later left the page.
          attempt.report({
            ...deliveryBeaconIdentity(streamTurnId, requestId, startTime),
            outcome: 'terminal',
            ...recoveryDiagnostics(),
            answer_rendered: true,
            answer_chars: renderedAnswerText().length,
            last_event_id: lastEventId,
            reconnect_attempts: reconnectAttempts,
            had_network_disconnect: totalReconnectAttempts > 0,
            elapsed_ms: Date.now() - startTime,
            transport: opts?.transport === 'subscribe' ? 'subscribe' : 'stream',
          });
          releaseController();
          onProgress?.({
            requestId,
            stage: ResponseStage.IDLE,
            progress: 100,
            currentActivity: 'Complete',
            startTime,
            estimatedDuration: 0,
            canCancel: false,
            streamedContent: fullText,
            toolMarkers: [...toolMarkers],
            activeTools: [],
          });
          return {
            id: `response_${Date.now()}`,
            content: fullText || '',
            timestamp: Date.now(),
            conversationId,
            metadata: {
              requires_questionnaire: true,
              questionnaire_data: {
                questions: chunk.questions || [],
                conversation_id: chunk.conversation_id || conversationId,
              },
            },
          };
        } else if (chunk.type === 'graph_complete') {
          // GET /subscribe uses this marker when it loses the worker heartbeat.
          // The attachment ended, but the job did not prove completion.
          if (chunk.tail_closed) {
            throw Object.assign(new Error('The response paused before completion. Please retry.'), {
              code: 'STREAM_TAIL_CLOSED',
            });
          }
          // The server's own end-of-turn marker. Everything after this in the
          // body is drain; a close BEFORE it is a dropped connection.
          sawTerminalEvent = true;

          // Stamp the moment the answer stopped arriving. The stage carried by
          // the terminal payload below never survives to the store — the send
          // path's progress handler overwrites it with STREAMING — so without
          // this there is no store-visible "answer done", only the post-turn
          // settle fifteen seconds later. `pending` says the turn left work that
          // comes back into this chat, so the answer is not the last word yet.
          resolveStore(conversationId).getState().markAnswerComplete(readTurnPending(chunk.pending));

          // Ensure all incomplete tools are marked as complete
          toolMarkers.forEach((marker, index) => {
            if (!marker.message) {
              toolMarkers[index] = { ...marker, message: `using ${marker.tool}`, success: true };
            } else if (marker.success === null || marker.success === undefined) {
              toolMarkers[index] = { ...marker, success: true };
            }
          });

          // Defer the terminal payload until the drip drains the last-arrived
          // buffer at server pace, so the reply's tail doesn't land in one big
          // dump. If the drip is already empty this fires synchronously.
          // Carries the node-gating fields like every token payload does — a
          // consumer that only shows conclusion_render text must be able to
          // apply the FINAL flush too, not just mid-stream tokens.
          scheduleTerminal({
            requestId,
            stage: ResponseStage.IDLE,
            progress: 100,
            currentActivity: 'Complete',
            startTime,
            estimatedDuration: 0,
            canCancel: false,
            streamedContent: fullText,
            conclusionContent: nodeTagged ? conclusionText : undefined,
            nodeTagged,
            toolMarkers: [...toolMarkers],
            activeTools: [],
          });
        } else if (chunk.type === 'response_reset') {
          // Worker died mid-stream and the backend is auto-recovering.
          // Clear all partial state so the resumed stream starts clean.
          fullText = '';
          conclusionText = '';
          answerStart = null;
          activityNote = undefined;
          nodeTagged = false;
          toolMarkers.length = 0;
          outputTokenCount = 0;
          toolResponseTokens = 0;
          toolsNodeCompleted = false;
          finalResponseStarted = false;
          activeTools.clear();

          // Phase 2: Reset lastEventId so reconnection after recovery doesn't
          // re-request old events from the prior attempt_id. The new worker
          // attempt starts a fresh stream with sequence 0.
          lastEventId = 0;
          const resetStore = resolveStore(conversationId).getState();
          if (resetStore.conversationId === conversationId) {
            resetStore.updateSSEConnectionState({ lastEventId: 0 });
          }

          onProgress?.({
            requestId,
            stage: ResponseStage.WAITING,
            progress: 30,
            currentActivity: 'Recovering...',
            startTime,
            estimatedDuration: 30000,
            canCancel: true,
            streamedContent: '',
            toolMarkers: [],
            activeTools: [],
            isStreamReset: true,
          });
        } else if (chunk.type === 'reply_reset') {
          // A model call that had begun the answer is being tried again, or
          // handed to another model, and will write the answer anew. Take back
          // only that answer (`takeBackAnswer`); the working text and the steps
          // stay, and the stream goes on from here.
          flushDrip();
          ({ fullText, conclusionText, answerStart } = takeBackAnswer({ fullText, conclusionText, answerStart }));
          streamedDisplayedLen = Math.min(streamedDisplayedLen, fullText.length);
          conclusionDisplayedLen = 0;
          latestTokenPayload = null;
          onProgress?.({
            requestId,
            stage: ResponseStage.STREAMING,
            progress: 75,
            currentActivity: 'Streaming...',
            startTime,
            estimatedDuration: 30000,
            canCancel: true,
            streamedContent: fullText,
            conclusionContent: nodeTagged ? conclusionText : undefined,
            nodeTagged,
            toolMarkers: [...toolMarkers],
            activeTools: Array.from(activeTools),
            activityNote,
            isReplyReset: true,
          });
        } else if (chunk.type === 'inbox_delivered') {
          // The running Code Mode turn read these mid-turn messages (conversation inbox).
          resolveStore(conversationId).getState().markInboxDelivered(Array.isArray(chunk.ids) ? chunk.ids : []);
        } else if (chunk.type === 'status') {
          // Status update from backend (e.g., video processing progress). A
          // hand-off or a quiet model also sets the pill's activity line.
          const note = statusActivityNote(chunk);
          if (note !== undefined) activityNote = note ?? undefined;
          const statusMessage = chunk.message || 'Processing...';
          onProgress?.({
            requestId,
            stage: ResponseStage.WAITING,
            progress: 50,
            currentActivity: statusMessage,
            startTime,
            estimatedDuration: 30000,
            canCancel: true,
            streamedContent: fullText,
            toolMarkers: [...toolMarkers],
            activeTools: Array.from(activeTools),
            activityNote,
          });
        } else if (chunk.type === 'overflow_recovery_started') {
          // Context overflow detected - recovery is starting
          // This will take ~35 seconds while Gemini generates a summary
          console.log('🔄 [chatbotApi] Overflow recovery started event received:', {
            chunk,
            timestamp: new Date().toISOString(),
            currentConversationId: conversationId,
          });
          
          const store = resolveStore(conversationId).getState();
          store.handleOverflowRecoveryStarted(chunk.original_conversation_id || conversationId);
          
          onProgress?.({
            requestId,
            stage: ResponseStage.WAITING,
            progress: 50,
            currentActivity: chunk.message || 'Creating a summary of our conversation...',
            startTime,
            estimatedDuration: 40000, // ~40 seconds for summary
            canCancel: false,
            streamedContent: fullText,
          });
        } else if (chunk.type === 'continuation_complete') {
          // Continuation conversation created successfully
          // Frontend should switch to new conversation and auto-retry
          console.log('✅ [chatbotApi] Continuation complete event received:', {
            newConversationId: chunk.conversation_id,
            retryMessagePreview: chunk.retry_message?.slice(0, 50) + '...',
            continuedFrom: chunk.continued_from,
            timestamp: new Date().toISOString(),
          });
          
          // This reader is done; the message is sent again in the new conversation,
          // whose own attempt reports it. Left registered, a pagehide filed this one
          // as page_unload.
          releaseController();
          const store = resolveStore(conversationId).getState();
          await store.handleContinuationComplete(
            chunk.conversation_id,
            chunk.retry_message,
            chunk.continued_from,
            chunk.continuation_chain || []
          );
          
          console.log('✅ [chatbotApi] handleContinuationComplete finished, returning early');
          
          // Return early - the retry will happen in the new conversation
          // Mark as continuation so _handleRealResponse knows not to add this message
          return {
            id: `continuation_${Date.now()}`,
            content: chunk.message || 'Conversation continued in a new thread.',
            timestamp: Date.now(),
            conversationId: chunk.conversation_id,
            metadata: {
              isContinuation: true, // Flag to skip adding this message in _handleRealResponse
            },
          };
        } else if (chunk.type === 'stream_interrupted') {
          // Stream was interrupted mid-response.
          // IMPORTANT UX: if the user explicitly clicked Stop, do not show the interruption banner.
          if (isStoppedByUserStreamInterruption(chunk)) {
            console.log('🛑 [chatbotApi] Stream cancelled by user (backend acknowledged).');
            // The Stop pressed in this tab aborted its own reader, so a reader that
            // reads the stop is a reattach, or another tab's Stop. It reports here and
            // lets go, like the interrupt below: left registered, the next pagehide
            // filed page_unload with an empty render over the person's own Stop.
            attempt.report({
              ...deliveryBeaconIdentity(streamTurnId, requestId, startTime),
              outcome: 'aborted',
              ...recoveryDiagnostics(),
              recovery_stop_reason: 'aborted',
              abort_reason: 'user_stop',
              answer_rendered: didRenderAnswer(),
              answer_chars: renderedAnswerText().length,
              last_event_id: lastEventId,
              reconnect_attempts: reconnectAttempts,
              had_network_disconnect: totalReconnectAttempts > 0,
              elapsed_ms: Date.now() - startTime,
              transport: opts?.transport === 'subscribe' ? 'subscribe' : 'stream',
            });
            releaseController();
            finalizeInProgressToolMarkers(toolMarkers); // stop any tool spinners immediately
            activeTools.clear();
            resolveStore(conversationId).getState().setActiveTools([]);

            onProgress?.({
              requestId,
              stage: ResponseStage.IDLE,
              progress: 100,
              currentActivity: 'Stopped',
              startTime,
              estimatedDuration: 0,
              canCancel: false,
              streamedContent: chunk.partial_response || fullText,
              toolMarkers: [...toolMarkers],
              activeTools: [],
            });

            return {
              id: `cancelled_${Date.now()}`,
              content: chunk.partial_response || fullText,
              timestamp: Date.now(),
              conversationId,
              metadata: {
                isInterrupted: true,
                error: chunk.error,
                isRetryable: false,
              },
            };
          }

          // Otherwise, surface to frontend for user-initiated retry
          console.error('❌ [chatbotApi] Stream interrupted:', {
            partialResponseLength: chunk.partial_response?.length || 0,
            error: chunk.error,
            isRetryable: chunk.is_retryable,
          });
          captureChatWarning('SSE stream_interrupted event', {
            error: chunk.error,
            isRetryable: chunk.is_retryable,
            partialResponseLength: chunk.partial_response?.length || 0,
            conversationId,
          });

          // An interrupt is a NON-DELIVERY, and until now it was the only one
          // that left no row. The turn row still reads success — the server's
          // graph finished, or a retry finished it — so nothing in our own data
          // moved while the person sat in front of a Retry button. It reached
          // Sentry and stopped there, which is the same shape as the PostHog-only
          // gap this beacon was built to close.
          attempt.report({
            ...deliveryBeaconIdentity(streamTurnId, requestId, startTime),
            outcome: 'interrupted',
            ...recoveryDiagnostics(),
            reconnect_attempts: reconnectAttempts,
            had_network_disconnect: totalReconnectAttempts > 0,
            interrupt_retryable: chunk.is_retryable !== false,
            // Partial text is evidence, not delivery. The Retry banner means
            // the turn did not finish for the person; answer_chars below keeps
            // how much reached the screen without letting a fragment count as
            // a delivered answer.
            answer_rendered: false,
            answer_chars: renderedAnswerText().length,
            last_event_id: lastEventId,
            elapsed_ms: Date.now() - startTime,
            transport: opts?.transport === 'subscribe' ? 'subscribe' : 'stream',
          });
          // This reader is finished, and releaseController() is NOT in a finally
          // — the success and catch paths each call it by hand, and both
          // interrupt paths returned without one. Left registered, the dead
          // controller is still in activeControllers, so the next pagehide walks
          // it and files a page_unload under the SAME key, overwriting the
          // interrupted row we just wrote with a cause that never happened. It
          // also makes the user's own Retry log a phantom supersede.
          releaseController();

          const store = resolveStore(conversationId).getState();
          store.handleStreamInterrupted(
            chunk.partial_response || fullText,
            chunk.error || 'Unknown error',
            chunk.is_retryable !== false, // Default to true
            message, // Original user message
            uploadedImages,
            isHidden,
            displayText,
            streamTurnId ?? opts?.turnId,
            // The server declared this turn interrupted and retryable, so its
            // checkpoint holds: one rejoin may happen without asking.
            { sendId: attempt.sendId, autoResume: true },
          );
          
          // Return partial response so it can be displayed
          return {
            id: `interrupted_${Date.now()}`,
            content: chunk.partial_response || fullText,
            timestamp: Date.now(),
            conversationId,
            metadata: {
              isInterrupted: true,
              error: chunk.error,
              isRetryable: chunk.is_retryable,
            },
          };
        } else if (chunk.type === 'error') {
          // Check if this is an unrecoverable overflow error
          if (chunk.error_type === 'overflow_unrecoverable' || chunk.requires_new_conversation) {
            console.error('❌ [chatbotApi] Unrecoverable overflow error:', chunk);
            
            const store = resolveStore(conversationId).getState();
            store.handleOverflowError(chunk.content || 'This conversation has become too complex. Please start a new conversation.');
            
            // Return the error response instead of throwing
            return {
              id: `error_${Date.now()}`,
              content: chunk.content || 'Conversation overflow - please start a new conversation.',
              timestamp: Date.now(),
              conversationId,
            };
          }
          
          // Resumable backend error (#4067 follow-up): the turn died mid-drive
          // but its DynamoDB checkpoint is intact, so a retry rejoins it. Surface
          // the calm Retry banner (same path as stream_interrupted) instead of
          // the dead-end "Something went wrong. Please start a new message." throw.
          // The Retry posts is_retry=true → _try_reuse_active_turn → crash-resume.
          if (chunk.resumable) {
            console.warn('🔄 [chatbotApi] Resumable error — offering retry-resume:', chunk.content);
            finalizeInProgressToolMarkers(toolMarkers);
            activeTools.clear();
            const store = resolveStore(conversationId).getState();
            store.setActiveTools([]);
            // The path that actually fires in production. On 2026-09-15 a
            // SoftTimeLimitExceeded killed two turns mid-drive during a Meta
            // HTTP-500 retry storm; both surfaced this banner, both were retried,
            // and both then completed — so the day's telemetry read 42/42
            // success for that account while a developer retried by hand. Same
            // beacon as the interrupt above: Retry reuses the logical turn for
            // checkpoint idempotency, but gets a new client attempt row. That
            // immutable row says the first attempt failed the person even if
            // the later summary ends in success.
            attempt.report({
              ...deliveryBeaconIdentity(streamTurnId, requestId, startTime),
              outcome: 'interrupted',
              ...recoveryDiagnostics(),
              reconnect_attempts: reconnectAttempts,
              had_network_disconnect: totalReconnectAttempts > 0,
              interrupt_retryable: true,
              answer_rendered: false,
              answer_chars: renderedAnswerText().length,
              last_event_id: lastEventId,
              elapsed_ms: Date.now() - startTime,
              transport: opts?.transport === 'subscribe' ? 'subscribe' : 'stream',
            });
            // Same reason as the interrupt path above: this reader is done, and
            // a controller left in activeControllers becomes a false page_unload
            // over the top of this row.
            releaseController();
            store.handleStreamInterrupted(
              fullText,
              chunk.content || 'The response was interrupted.',
              true, // resumable ⇒ retryable
              message, // Original user message
              uploadedImages,
              isHidden,
              displayText,
              streamTurnId ?? opts?.turnId,
              // Resumable is the server saying its worker exited with the
              // checkpoint intact (the soft-limit case): rejoin once unasked.
              { sendId: attempt.sendId, autoResume: true },
            );
            onProgress?.({
              requestId,
              stage: ResponseStage.IDLE,
              progress: 100,
              currentActivity: 'Paused',
              startTime,
              estimatedDuration: 0,
              canCancel: false,
              streamedContent: fullText,
              toolMarkers: [...toolMarkers],
              activeTools: [],
            });
            return {
              id: `interrupted_${Date.now()}`,
              content: fullText,
              timestamp: Date.now(),
              conversationId,
              metadata: {
                isInterrupted: true,
                error: chunk.content,
                isRetryable: true,
              },
            };
          }

          // Finalize any pending tool spinners before throwing
          finalizeInProgressToolMarkers(toolMarkers);
          activeTools.clear();
          onProgress?.({
            requestId,
            stage: ResponseStage.IDLE,
            progress: 100,
            currentActivity: 'Error',
            startTime,
            estimatedDuration: 0,
            canCancel: false,
            streamedContent: fullText,
            toolMarkers: [...toolMarkers],
            activeTools: [],
          });
          throw new Error(chunk.content || 'Streaming error');
        }
          }
          
          // A body that ends without the server's terminal event is a dropped
          // connection wearing a success costume: the generator returns
          // normally on `done`, so nothing here throws and the turn used to
          // finalize with whatever partial text had arrived — no error, no
          // Resume banner, no reconnect.
          if (!sessionCurrent()) throw new DOMException('Executive session changed', 'AbortError');
          const streamDropped = sawAnyEvent && !sawTerminalEvent;

          // Did this turn actually put an answer on the screen?
          //
          // `outcome` above cannot answer that, and the gap is not theoretical:
          // it asks whether a TERMINAL EVENT arrived, and any producer can send
          // one. When a background pass shared this conversation's stream key it
          // sent its own `graph_complete` mid-turn, so `sawTerminalEvent` went
          // true, the turn was filed `outcome='terminal'` — a success — and the
          // user sat looking at reasoning with no reply. Over 8 days of prod
          // that metric recorded 285 terminal, 9 aborted and ZERO dropped,
          // through the week the drop rate doubled.
          //
          // So measure the artifact, not the protocol. Node-gating already
          // separates the two: only `conclusion_render` tokens are user-facing,
          // and `conclusionText` holds exactly that segment, which is the same
          // text the UI renders as the reply. Mirror its own fallback — a stream
          // that never tagged a node has no conclusion segment to gate on, so
          // `fullText` is what the user saw. The rule itself lives in
          // `renderedAnswerText` / `didRenderAnswer` beside `streamTurnId`, so
          // this path and the abort path cannot disagree about what an answer is.
          const answerText = renderedAnswerText();

          // Report the browser's view of how this stream ended, ALWAYS and
          // independently of what we do about it. This is the measurement the
          // product never had: pair it with the server's 📡 StreamExit line on
          // turn_id and "worker finished but the browser didn't get it" stops
          // being a hypothesis. Emitted even when nothing is wrong, so the
          // delivery RATE is knowable rather than just its existence.
          //
          // `outcome` keeps its original meaning on purpose. Redefining a live
          // field in place would silently rewrite the history it is compared
          // against; `answer_rendered` is the honest signal and arrives beside
          // it, so the two can be read together and the old series stays whole.
          const closeOutcome = streamCloseOutcome(
            streamDropped,
            streamDropped && RETRY_ON_DROPPED_STREAM,
          );
          // Decided once, here, so the beacon and the recovery below cannot
          // disagree about whether this close delivered anything.
          const rehydrate = shouldRehydrateAfterClose(streamDropped, didRenderAnswer());
          if (closeOutcome !== null) {
            attempt.report({
              ...deliveryBeaconIdentity(streamTurnId, requestId, startTime),
              outcome: closeOutcome,
              ...recoveryDiagnostics(),
              answer_rendered: didRenderAnswer(),
              answer_chars: answerText.length,
              last_event_id: lastEventId,
              reconnect_attempts: reconnectAttempts,
              had_network_disconnect: totalReconnectAttempts > 0,
              transport: opts?.transport === 'subscribe' ? 'subscribe' : 'stream',
              elapsed_ms: Date.now() - startTime,
              ...(rehydrate ? { recovery: 'rehydrate' } : {}),
            });
          }
          if (rehydrate && sessionCurrent()) {
            this.rehydrateAfterEmptyClose(conversationId, rehydrateReceipt());
          }

          // PROVISIONAL(#7443) — see docs/PROVISIONAL_7443.md before changing.
          // THEORY: mid-turn stream drops are frequent enough to be what users
          //   report as "Leo froze, I had to start a new conversation".
          // CONFIRMED IF: Leonardo/Celery StreamDropped (server) and this
          //   event's answer_rendered=false (client) both run materially above
          //   zero once deployed.
          // REFUTED IF: both sit at ~0 over a week of normal traffic. Then the
          //   drops are not happening, this branch is dead weight, and the
          //   whole PROVISIONAL(#7443) set should be reverted rather than left
          //   to rot as an unexplained retry path.
          // Both criteria read answer_rendered, NOT outcome. They named
          //   outcome='dropped' until 2026-09-05, when it turned out to be
          //   blind to the failure that dominated: `outcome` asks whether a
          //   terminal EVENT arrived, and a background pass sharing this
          //   conversation's stream key sent one mid-turn, so frozen turns were
          //   filed as successes. 285 terminal / 9 aborted / 0 dropped across
          //   8 days, through the week the server-side rate went 5.3% -> 13.7%.
          //   Applied to `outcome`, REFUTED would have fired on a blind zero.
          // KILL SWITCH: flip RETRY_ON_DROPPED_STREAM to false. Detection and
          //   telemetry above keep running; only the behavior change stops.
          if (streamDropped && RETRY_ON_DROPPED_STREAM) {
            throw new Error('connection closed before the turn completed');
          }

          // Stream completed successfully
          streamComplete = true;

          // Safety net: if stream ended without graph_complete, finalize any
          // tool markers still showing as in-progress (success === null).
          // This handles network drops, server crashes, or missing graph_complete.
          if (toolMarkers.some(m => m.success === null || m.success === undefined)) {
            finalizeInProgressToolMarkers(toolMarkers);
            onProgress?.({
              requestId,
              stage: ResponseStage.IDLE,
              progress: 100,
              currentActivity: 'Complete',
              startTime,
              estimatedDuration: 0,
              canCancel: false,
              streamedContent: fullText,
              toolMarkers: [...toolMarkers],
              activeTools: [],
            });
            activeTools.clear();
          }

        } catch (streamError) {
          if (!sessionCurrent()) throw new DOMException('Executive session changed', 'AbortError');
          // Retain the silence preceding the failure even after replay succeeds.
          lastErrorEventAgeMs = Math.max(0, Date.now() - lastStreamEventAt);
          // Check if this is an abort (user cancelled)
          if (streamError instanceof Error && streamError.name === 'AbortError') {
            // Reported so the client's accounting of stream endings is
            // COMPLETE. The server cannot tell a user abort from a network
            // drop — both arrive as GeneratorExit, and the cancel flag it
            // would need is consumed by the worker and TTLs out — so the
            // server's StreamDropped carries a benign floor of stop-button
            // clicks, tab closes and navigations. This is the only place that
            // floor is nameable, which is why the verdict in
            // docs/PROVISIONAL_7443.md is keyed on the client, not the server.
            // Carries the same delivery fields as the normal close, so the two
            // paths are ONE population: a filter on answer_chars that finds it
            // undefined here would silently drop every abort from the sample.
            // An abort is not a delivery failure, but it is not automatically a
            // non-delivery either — a user who stops the stream AFTER the reply
            // has rendered got their answer, and one who stops before it did
            // not. Recording which lets the delivery rate exclude aborts
            // without assuming they were all empty.
            // WHY it aborted, not just that it did. Without this the delivery
            // rate charges us for the person who pressed Stop and the reader a
            // second one superseded, and buries the cases that can be ours
            // (`unknown`, and `page_unload` after a long silence — normally
            // reported from pagehide, not here) among them. Read once — a
            // second abort on this conversation gets its own label rather than
            // inheriting this one.
            attempt.report({
              ...deliveryBeaconIdentity(streamTurnId, requestId, startTime),
              outcome: 'aborted',
              ...recoveryDiagnostics(),
              recovery_stop_reason: 'aborted',
              abort_reason: this.takeAbortReason(streamController),
              answer_rendered: didRenderAnswer(),
              answer_chars: renderedAnswerText().length,
              last_event_id: lastEventId,
              elapsed_ms: Date.now() - startTime,
            });
            // Don't retry aborts - rethrow to outer catch
            throw streamError;
          }

          // Check if this is a network/connection error that we can retry
          // Lower-cased once: the same message arrives capitalised or not
          // depending on whether it came from us, from fetch, or from
          // `response.statusText`.
          const streamErrorText = streamError instanceof Error
            ? streamError.message.toLowerCase()
            : '';
          // A refused HTTP request is not a network error merely because its
          // message mentions a connection. Retry gateway timeouts only on GET;
          // a timed-out initial POST may already have submitted the work.
          const streamErrorStatus = (streamError as { status?: number })?.status;
          const isNetworkError = streamErrorStatus !== undefined
            ? streamErrorStatus === 502 || (readOnlyRecovery && streamErrorStatus === 504)
            : streamError instanceof TypeError || (streamError instanceof Error && (
              streamErrorText.includes('network') ||
              streamErrorText.includes('fetch') ||
              streamErrorText.includes('connection') ||
              streamErrorText.includes('bad gateway')
            ));
          
          // Record bounded categories only; exception messages can contain
          // customer content, credentials, or URLs.
          const isStall = (streamError as { isStall?: boolean })?.isStall === true;
          clientErrorKind = (streamError as { code?: string })?.code === 'STREAM_TAIL_CLOSED'
            ? 'worker_lost' : isStall ? 'stall' : streamErrorStatus !== undefined
            ? 'http' : isNetworkError ? 'network' : streamError instanceof Error ? 'client' : 'unknown';
          clientErrorStatus = streamErrorStatus;
          if (!readOnlyRecovery && streamErrorStatus === 502) postMayHaveLanded = true;
          if (isStall) stallReconnects++;
          reconnectStartedAt ??= Date.now();
          const reconnectElapsedMs = Date.now() - reconnectStartedAt;

          if (clientErrorKind === 'worker_lost') {
            // Heartbeat loss is not a dead turn: the server may have re-queued
            // it, or finished it while this reader was cut off. Ask about THIS
            // turn before choosing between following, painting and asking.
            workerLossStartedAt ??= Date.now();
            const status = await NewConversationService.getStreamStatus(conversationId);
            const decision = workerLossRecovery({
              turnState: serverTurnState(status, streamTurnId ?? opts?.turnId),
              reattachCount: workerLossReattaches,
              elapsedSinceLossMs: Date.now() - workerLossStartedAt,
            });
            if (decision === 'reattach') {
              workerLossReattaches++;
              readOnlyRecovery = true;
              onProgress?.({
                requestId,
                stage: ResponseStage.WAITING,
                progress: 50,
                currentActivity: 'Reconnecting...',
                startTime,
                estimatedDuration: 30000,
                canCancel: true,
                streamedContent: fullText,
                toolMarkers: [...toolMarkers],
                activeTools: Array.from(activeTools),
              });
              await new Promise(resolve => setTimeout(resolve, WORKER_LOSS_REATTACH_DELAY_MS));
              continue;
            }
            if (decision === 'show_saved' && sessionCurrent()) {
              // The turn finished while this reader was cut off, so its answer
              // is in the save. This attempt is a drop; the rehydrate's own
              // receipt says whether loading the save painted a reply, and the
              // Retry banner comes up if it did not.
              const rendered = didRenderAnswer();
              attempt.report({
                ...deliveryBeaconIdentity(streamTurnId, requestId, startTime),
                outcome: rendered ? 'terminal' : 'dropped',
                ...recoveryDiagnostics(),
                answer_rendered: rendered,
                answer_chars: renderedAnswerText().length,
                last_event_id: lastEventId,
                elapsed_ms: Date.now() - startTime,
                transport: 'subscribe',
                ...(rendered ? {} : { recovery: 'rehydrate' }),
              });
              if (!rendered) {
                this.rehydrateAfterEmptyClose(conversationId, rehydrateReceipt(() => {
                  resolveStore(conversationId).getState().handleStreamInterrupted(
                    '',
                    'The response was interrupted.',
                    true,
                    message,
                    uploadedImages,
                    isHidden,
                    displayText,
                    streamTurnId ?? opts?.turnId,
                    { sendId: attempt.sendId },
                  );
                }));
              }
              streamComplete = true;
              break;
            }
            // 'banner': fall through. A worker loss is not a network error, so
            // the path below stops as non-retryable and the store offers Retry,
            // which rejoins the turn's checkpoint. Never unasked: a dead
            // worker's task can be redelivered minutes later, and a rejoin in
            // the meantime would run the same turn twice.
          }
          // The post-answer claim race refuses with 409 conversation_turn_changed
          // and releases its idempotency claim before anything is queued, so
          // retrying this same send cannot run the turn twice. A 409 for an
          // empty reattach is a different code and is not retried.
          const turnChangedDelay = turnChangedRetryDelayMs(
            {
              status: streamErrorStatus,
              code: (streamError as { code?: string })?.code,
            },
            turnChangedRetries,
          );
          if (turnChangedDelay !== null && !readOnlyRecovery) {
            turnChangedRetries += 1;
            await new Promise(resolve => setTimeout(resolve, turnChangedDelay));
            continue;
          }
          // Retain the existing pre-acknowledgement 502 policy (which still
          // relies on server idempotency). Other ambiguous failed POSTs need
          // explicit recovery rather than automatic resubmission.
          const canRetry = isNetworkError && (readOnlyRecovery || streamErrorStatus === 502);
          if (
            canRetry &&
            reconnectAttempts < MAX_RECONNECT_ATTEMPTS &&
            totalReconnectAttempts < MAX_TOTAL_RECONNECTS &&
            stallReconnects <= MAX_STALL_RECONNECTS &&
            reconnectElapsedMs < MAX_RECONNECT_WINDOW_MS
          ) {
            reconnectAttempts++;
            totalReconnectAttempts++;
            if (!hadNetworkDisconnect) {
              const store = resolveStore(conversationId).getState();
              if (store.conversationId === conversationId) {
                store.handleSSEDisconnect(attempt.sendId);
                store.updateSSEConnectionState({ lastEventId });
              }
              hadNetworkDisconnect = true;
            }
            const store = resolveStore(conversationId).getState();
            if (store.conversationId === conversationId) {
              store.updateStreamInterruptedState({ lastReconnectAttemptAt: Date.now() });
            }
            console.log(`🔄 [chatbotApi] Connection lost, attempting reconnection ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS} (lastEventId=${lastEventId || 0})`);
            
            // Show reconnection feedback to user
            onProgress?.({
              requestId,
              stage: ResponseStage.WAITING,
              progress: 50,
              currentActivity: `Reconnecting... (attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`,
              startTime,
              estimatedDuration: 30000,
              canCancel: true,
              streamedContent: fullText,
              toolMarkers: [...toolMarkers],
              activeTools: Array.from(activeTools),
            });
            
            // Wait before retrying
            // Jittered. The backoff was flat and unjittered when only genuine
            // network errors reached it; the dropped-stream throw above is a
            // new, far more common trigger, and a shared cause (an ALB or
            // gateway blip severing many streams at once) would otherwise march
            // every affected client back in lockstep. +/-25% is enough to
            // spread the herd without materially changing the felt latency.
            const backoffMs = RECONNECT_DELAY_MS * reconnectAttempts;
            const jitteredBackoffMs = backoffMs * (0.75 + Math.random() * 0.5);
            await new Promise(resolve => setTimeout(resolve, jitteredBackoffMs));
            
            // Continue to next iteration of while loop to retry
            continue;
          }
          
          recoveryStopReason = !canRetry ? 'non_retryable'
            : reconnectElapsedMs >= MAX_RECONNECT_WINDOW_MS ? 'time_limit' : 'attempt_limit';
          // Non-recoverable error or max retries exceeded
          throw streamError;
        }
      }

      // Clear abort controller on successful completion
      releaseController();

      // Drop any pending drip — the terminal path already fired the final state.
      cancelDripRaf();

      // After stream completes, return final response with all tool markers
      // Note: suggestedResponses will be parsed from fullText on the frontend
      return {
        id: `response_${Date.now()}`,
        content: fullText, // Contains raw XML, will be parsed on frontend
        timestamp: Date.now(),
        conversationId,
      };
    } catch (error) {
      // Handle user-initiated abort
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('[chatbotApi] Stream aborted by user');
        finalizeInProgressToolMarkers(toolMarkers); // stop any tool spinners immediately
        releaseController();
        resolveStore(conversationId).getState().setActiveTools([]);

        onProgress?.({
          requestId,
          stage: ResponseStage.IDLE,
          progress: 100,
          currentActivity: 'Stopped',
          startTime,
          estimatedDuration: 0,
          canCancel: false,
          streamedContent: fullText,
          toolMarkers: [...toolMarkers],
          activeTools: [],
        });
        // Return partial content that was streamed
        return {
          id: `response_${Date.now()}`,
          content: fullText,
          timestamp: Date.now(),
          conversationId,
          metadata: {
            isInterrupted: true,
          },
        };
      }

      clientErrorKind ??= 'client';

      // Every thrown non-abort path converges here, and until now none of them
      // reported. The two banner sites above are the resumable ones; a
      // NON-resumable crash, a network error that exhausts reconnects, and a
      // failed initial POST all rethrow to the store, which shows the person the
      // identical Retry banner from `chatbotStore.ts` — with no row written. So
      // the fix has to hang off the banner, not off two of the four places that
      // raise it, or a whole class of mid-drive crash still reads as a perfect
      // night. Guarded, because the dropped-stream throw reports before it
      // throws and must keep its own `dropped` row.
      if (!attempt.reported) {
        attempt.report({
          ...deliveryBeaconIdentity(streamTurnId, requestId, startTime),
          outcome: 'interrupted',
          ...recoveryDiagnostics(),
          reconnect_attempts: reconnectAttempts,
          had_network_disconnect: totalReconnectAttempts > 0,
          // The store offers Retry on these, so from the person's seat they are
          // the retryable case; the server decides whether it rejoins.
          interrupt_retryable: true,
          answer_rendered: false,
          answer_chars: renderedAnswerText().length,
          last_event_id: lastEventId,
          elapsed_ms: Date.now() - startTime,
          transport: opts?.transport === 'subscribe' ? 'subscribe' : 'stream',
        });
      }

      const deliveryContext = {
        acknowledged: readOnlyRecovery || Boolean(streamTurnId),
        postMayHaveLanded,
      };
      markDeliveryError(error, deliveryContext);
      releaseController();
      // Report and release before invoking UI callbacks that may themselves throw.
      finalizeInProgressToolMarkers(toolMarkers);
      resolveStore(conversationId).getState().setActiveTools([]);
      onProgress?.({
        requestId, stage: ResponseStage.IDLE, progress: 100, currentActivity: 'Error',
        startTime, estimatedDuration: 0, canCancel: false, streamedContent: fullText,
        toolMarkers: [...toolMarkers], activeTools: [],
      });
      if (error instanceof Error) {
        throw error;
      }
      const streamingFailed = new Error('Streaming failed');
      markDeliveryError(streamingFailed, deliveryContext);
      throw streamingFailed;
    }
  }

  /**
   * Cancel the request for a specific conversation (or all if omitted).
   */
  cancelRequest(conversationId?: string): void {
    if (conversationId) {
      const controller = this.activeControllers.get(executiveResourceKey(conversationId));
      if (controller) {
        console.log(`[chatbotApi] Cancelling active stream for conversationId: ${conversationId}`);
        this.abortWithReason(controller, 'user_stop');
        this.activeControllers.delete(executiveResourceKey(conversationId));
      } else {
        console.log(`[chatbotApi] cancelRequest called for conversationId: ${conversationId}, but no active controller found`);
      }
    } else {
      console.log(`[chatbotApi] cancelRequest called with no conversationId — aborting all ${this.activeControllers.size} active streams`);
      this.activeControllers.forEach((controller) =>
        this.abortWithReason(controller, 'user_stop'));
      this.activeControllers.clear();
    }
  }


  /**
   * Submit approval for a pending task
   */
  async submitApproval(
    conversationId: string,
    approvalData: any,
    approved: boolean = true
  ): Promise<ChatbotApiResponse> {
    const approvalRequest = {
      message: 'approve',
      conversation_id: conversationId,
      is_approval: true,
      approved: approved,
      approval_data: approvalData,
    };

    try {
      const result = await NewConversationService.approve(conversationId, approvalRequest);

      // Extract response message
      let assistantResponse = result.response;
      if (typeof assistantResponse === 'string') {
        try {
          const parsedResponse = JSON.parse(assistantResponse);
          assistantResponse = parsedResponse.message || parsedResponse.answer || assistantResponse;
        } catch (error) {
          // Use original if parsing fails
        }
      }

      return {
        id: result.conversation_id ?? `response_${Date.now()}`,
        content: assistantResponse || 'Task approved successfully.',
        timestamp: Date.now(),
        conversationId: result.conversation_id,
      };
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : 'Failed to submit approval'
      );
    }
  }

  /**
   * Submit questionnaire answers and stream the agent's continuation.
   * Uses the same SSE streaming pattern as sendMessageStream.
   */
  async submitQuestionnaireAnswersStream(
    conversationId: string,
    answers: Array<{ question_id: string; selected_option: string | null; freeform_text: string | null }>,
    onProgress?: ProgressCallback,
  ): Promise<ChatbotApiResponse> {
    const startTime = Date.now();
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    let fullText = '';
    // Node-gated answer isolation on the questionnaire-resume stream — parity
    // with the main stream. The resumed graph runs meat → … → conclusion_render,
    // so only render-node tokens are the user-facing reply; everything else is
    // working/thinking. Without this, the resume stream would carry no node
    // signal and the fail-safe gate would suppress its answer until finalize.
    let conclusionText = '';
    let nodeTagged = false;
    let outputTokenCount = 0;
    const activeTools = new Set<string>();
    const toolMarkers: Array<{ position: number; tool: string; runId?: string; message?: string; success?: boolean | null }> = [];

    // Same one-live-reader contract as sendMessageStream: supersede (abort) any
    // existing reader for this conversation, and only clear our own registration.
    const resourceKey = executiveResourceKey(conversationId);
    const supersededController = this.activeControllers.get(resourceKey);
    if (supersededController) {
      console.warn(`[chatbotApi] Superseding active stream for conversationId: ${conversationId}`);
      this.abortWithReason(supersededController, 'superseded');
    }
    const streamController = new AbortController();
    this.activeControllers.set(resourceKey, streamController);
    const releaseController = () => {
      if (this.activeControllers.get(resourceKey) === streamController) {
        this.activeControllers.delete(resourceKey);
      }
    };

    try {
      onProgress?.({
        requestId,
        stage: ResponseStage.WAITING,
        progress: 10,
        currentActivity: 'Leo is processing your answers...',
        startTime,
        estimatedDuration: 30000,
        canCancel: false,
        streamedContent: '',
        toolMarkers: [],
      });

      for await (const { data: chunk } of NewConversationService.streamQuestionnaire(
        conversationId,
        answers,
        streamController.signal,
      )) {
        if (chunk.type === 'keepalive') {
          continue;
        } else if (chunk.type === 'token') {
          fullText += chunk.content || '';
          // Isolate the render-node segment (the user-facing answer), same as
          // the main stream handler.
          if (typeof chunk.node === 'string' && chunk.node) {
            nodeTagged = true;
            // Same render-node rule as the main stream: conclusion_render only
            // (code_model is thinking narration, owned by the WorkingSection).
            if (chunk.node === 'conclusion_render') {
              conclusionText += chunk.content || '';
            }
          }
          outputTokenCount++;

          onProgress?.({
            requestId,
            stage: ResponseStage.STREAMING,
            progress: 75,
            currentActivity: `Streaming...`,
            startTime,
            estimatedDuration: 30000,
            canCancel: true,
            streamedContent: fullText,
            conclusionContent: nodeTagged ? conclusionText : undefined,
            nodeTagged,
            toolMarkers: [...toolMarkers],
          });
        } else if (chunk.type === 'tool_start') {
          const toolName = chunk.tool || 'unknown';
          const toolMessage = chunk.message || `using ${toolName}`;
          activeTools.add(toolName);
          toolMarkers.push({
            position: fullText.length,
            tool: toolName,
            runId: chunk.run_id,
            message: toolMessage,
            success: null,
          });
          onProgress?.({
            requestId,
            stage: ResponseStage.TOOL_CALLING,
            progress: 50,
            currentActivity: toolMessage,
            startTime,
            estimatedDuration: 30000,
            canCancel: true,
            streamedContent: fullText,
            toolMarkers: [...toolMarkers],
            activeTools: Array.from(activeTools),
          });
        } else if (chunk.type === 'tool_end') {
          const toolName = chunk.tool || 'unknown';
          activeTools.delete(toolName);
          const markerIdx = toolMarkers.findIndex(m => m.runId === chunk.run_id || m.tool === toolName);
          if (markerIdx !== -1) {
            toolMarkers[markerIdx] = {
              ...toolMarkers[markerIdx],
              message: chunk.message || toolMarkers[markerIdx].message,
              success: chunk.success === true ? true : chunk.success === false ? false : null,
            };
          }
          // Extract execution todo from tool_end (mirrors sendMessageStream logic)
          const executionTodo = extractExecutionTodoFromStreamChunk(chunk);
          if (executionTodo) {
            const store = resolveStore(conversationId).getState();
            if (store.conversationId === conversationId) {
              store.setExecutionTodo(executionTodo);
            }
          }
        } else if (chunk.type === 'structured_questions') {
          // Chained questionnaire — the agent called ask_structured_questions
          // again during the resume (e.g., rundown nudge triage after transition).
          // Handle identically to sendMessageStream: mark tools complete, return
          // with questionnaire metadata so the store renders the next wizard.
          console.log('[chatbotApi] Chained structured questions received:', chunk.questions?.length, 'questions');
          toolMarkers.forEach((marker, index) => {
            if (marker.success === null || marker.success === undefined) {
              toolMarkers[index] = { ...marker, success: true };
            }
          });
          onProgress?.({
            requestId,
            stage: ResponseStage.IDLE,
            progress: 100,
            currentActivity: 'Complete',
            startTime,
            estimatedDuration: 0,
            canCancel: false,
            streamedContent: fullText,
            toolMarkers: [...toolMarkers],
            activeTools: [],
          });

          releaseController();
          return {
            id: `response_${Date.now()}`,
            content: fullText || '',
            timestamp: Date.now(),
            conversationId,
            metadata: {
              requires_questionnaire: true,
              questionnaire_data: {
                questions: chunk.questions || [],
                conversation_id: chunk.conversation_id || conversationId,
              },
            },
          };
        } else if (chunk.type === 'graph_complete') {
          toolMarkers.forEach((marker, index) => {
            if (marker.success === null || marker.success === undefined) {
              toolMarkers[index] = { ...marker, success: true };
            }
          });
          onProgress?.({
            requestId,
            stage: ResponseStage.IDLE,
            progress: 100,
            currentActivity: 'Complete',
            startTime,
            estimatedDuration: 0,
            canCancel: false,
            streamedContent: fullText,
            toolMarkers: [...toolMarkers],
            activeTools: [],
          });
        } else if (chunk.type === 'error') {
          throw new Error(chunk.content || 'Streaming error');
        }
      }

      releaseController();

      return {
        id: `response_${Date.now()}`,
        content: fullText,
        timestamp: Date.now(),
        conversationId,
      };
    } catch (error) {
      releaseController();
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Questionnaire streaming failed');
    }
  }

  /**
   * Get chatbot health status (using new conversation endpoint)
   */
  async getHealthStatus(): Promise<{ status: 'healthy' | 'degraded' | 'down'; message?: string }> {
    try {
      // Cheapest liveness probe of the new-conversations endpoint: one page of
      // one item, not the full-history download getAll() does.
      await NewConversationService.getPage({ limit: 1 });
      return { status: 'healthy' };
    } catch (error) {
      return { 
        status: 'down', 
        message: error instanceof Error ? error.message : 'Network error' 
      };
    }
  }

}

// Export singleton instance
export const chatbotApi = new ChatbotApiService();
export default chatbotApi;
